import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import api, { PaginatedResponse, PaginationParams } from '@/config/api';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface Product {
  id: number;
  sku: string;
  name: string;
  category: string;
  uom: string;
  avg_cost: number;
  sell_price: number;
  on_hand: number;
  reorder_point: number;
  status: 'active' | 'inactive';
  description?: string;
  vendor_id?: number;
  lot_tracked: boolean;
  catch_weight: boolean;
  created_at: string;
  updated_at: string;
}

export interface StockLevel {
  id: number;
  product_id: number;
  product_name: string;
  product_sku: string;
  location: string;
  on_hand: number;
  allocated: number;
  available: number;
  /** Real reorder point from the product record; null = not configured. */
  reorder_point: number | null;
  last_movement: string;
  lots?: LotDetail[];
}

export interface LotDetail {
  lot_number: string;
  quantity: number;
  expiry_date: string | null;
  received_date: string;
}

export interface Order {
  id: number;
  order_number: string;
  customer_id: number;
  customer_name: string;
  order_date: string;
  status: 'draft' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  total: number;
  item_count: number;
  delivery_date: string | null;
  notes?: string;
  lines: OrderLine[];
}

export interface OrderLine {
  id: number;
  product_id: number;
  product_name: string;
  product_sku: string;
  quantity: number;
  unit_price: number;
  total: number;
  // Extended fields from /sales/orders/{id} detail endpoint
  line_number?: number;
  description?: string;
  qty_ordered?: number | null;
  qty_picked?: number | null;
  qty_shipped?: number | null;
  extension?: number | null;
}

export interface PurchaseOrder {
  id: number;
  po_number: string;
  vendor_id: number;
  vendor_name: string;
  order_date: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'receiving' | 'completed' | 'cancelled';
  total: number;
  expected_delivery: string | null;
  approved_by?: string;
  approved_at?: string;
  notes?: string;
  lines: POLine[];
}

export interface POLine {
  id: number;
  product_id: number;
  product_name: string;
  product_sku: string;
  quantity_ordered: number;
  quantity_received: number;
  unit_cost: number;
  total: number;
}

export interface Vendor {
  id: number;
  code: string;
  name: string;
  vendor_type: string;
  payment_terms: string;
  rating: number;
  certification_status: 'certified' | 'pending' | 'expired' | 'none';
  contact_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  certifications: VendorCertification[];
}

export interface VendorCertification {
  id: number;
  cert_type: string;
  cert_number: string;
  issued_date: string;
  expiry_date: string;
  status: 'active' | 'expired' | 'revoked';
}

export interface Customer {
  id: number;
  code: string;
  name: string;
  customer_type: string;
  payment_terms: string;
  balance: number;
  health_score: number;
  credit_limit: number;
  contact_name?: string;
  email?: string;
  phone?: string;
  billing_address?: string;
  shipping_address?: string;
  portal_status?: string;
  credit_hold?: boolean;
  credit_hold_reason?: string;
  tax_exempt?: boolean;
  tax_id?: string;
  exemption_type?: string;
  salesperson?: string;
  pricing_tier?: string;
  notes?: string;
  portal_approved_by?: string;
  portal_approved_at?: string;
  portal_rejected_by?: string;
  portal_rejected_at?: string;
  portal_rejection_reason?: string;
  created_at?: string;
  updated_at?: string;
}

export interface GLEntry {
  id: number;
  entry_number: string;
  date: string;
  description: string;
  account_code: string;
  account_name: string;
  debit: number;
  credit: number;
  reference?: string;
  created_by: string;
}

export interface TrialBalanceRow {
  account_code: string;
  account_name: string;
  account_type: string;
  debit_balance: number;
  credit_balance: number;
}

export interface PnLRow {
  category: string;
  account_code: string;
  account_name: string;
  amount: number;
  budget?: number;
  variance?: number;
}

export interface DashboardKPIs {
  revenue_30d: number;
  revenue_trend: number;
  orders_30d: number;
  orders_trend: number;
  inventory_value: number;
  inventory_trend: number;
  gross_margin_pct: number;
  margin_trend: number;
  sales_trend: { date: string; revenue: number; orders: number }[];
  top_products: { product_name: string; sku: string; revenue: number; quantity: number }[];
  low_stock_alerts: { product_name: string; sku: string; on_hand: number; reorder_point: number }[];
  recent_orders: { order_number: string; customer_name: string; total: number; status: string; date: string }[];
  upcoming_deliveries: { po_number: string; vendor_name: string; expected_delivery: string; item_count: number }[];
  compliance_alerts: { type: string; message: string; severity: 'low' | 'medium' | 'high' | 'critical'; date: string }[];
}

export interface Employee {
  id: number;
  employee_id: string;
  name: string;
  department: string;
  role: string;
  status: 'active' | 'inactive' | 'on_leave';
  hire_date: string;
  email?: string;
  phone?: string;
}

export interface TimeEntry {
  id: number;
  employee_id: number;
  employee_name: string;
  date: string;
  hours: number;
  task: string;
  notes?: string;
}

export interface Recall {
  id: number;
  recall_number: string;
  product_name: string;
  lot_numbers: string[];
  reason: string;
  status: 'active' | 'resolved' | 'monitoring';
  initiated_date: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  affected_quantity: number;
}

export interface Audit {
  id: number;
  audit_type: string;
  auditor: string;
  date: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'findings';
  score?: number;
  findings_count: number;
  next_audit_date?: string;
}

export interface ReceivingLine {
  po_line_id: number;
  product_name: string;
  product_sku: string;
  quantity_ordered: number;
  quantity_previously_received: number;
  quantity_received: number;
  lot_number: string;
  condition: 'good' | 'damaged' | 'rejected';
  catch_weight?: number;
}

export interface FinancialReport {
  report_type: 'trial_balance' | 'income_statement' | 'balance_sheet' | 'cash_flow';
  period_start: string;
  period_end: string;
  generated_at: string;
  sections: ReportSection[];
  totals: Record<string, number>;
}

export interface ReportSection {
  name: string;
  rows: ReportRow[];
  subtotal: number;
}

export interface ReportRow {
  account_code: string;
  account_name: string;
  amount: number;
  budget?: number;
  prior_period?: number;
}

export interface UserAccount {
  id: number;
  username: string;
  display_name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive' | 'locked';
  last_login: string | null;
  mfa_enabled: boolean;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Generic fetcher helpers
// ---------------------------------------------------------------------------

async function fetchPaginated<T>(
  url: string,
  params?: PaginationParams & Record<string, any>,
): Promise<PaginatedResponse<T>> {
  const { data } = await api.get<PaginatedResponse<T>>(url, { params });
  return data;
}

async function fetchOne<T>(url: string): Promise<T> {
  const { data } = await api.get<T>(url);
  return data;
}

async function fetchList<T>(
  url: string,
  params?: Record<string, any>,
): Promise<PaginatedResponse<T>> {
  const { data } = await api.get<T[]>(url, { params });
  const items = Array.isArray(data) ? data : [];
  return { items, total: items.length, page: 1, page_size: items.length, pages: 1 };
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export function useProducts(params?: PaginationParams & { category?: string; status?: string }) {
  return useQuery({
    queryKey: ['products', params],
    queryFn: () => fetchPaginated<Product>('/products', params),
  });
}

export function useProduct(id: number, options?: Partial<UseQueryOptions<Product>>) {
  return useQuery({
    queryKey: ['products', id],
    queryFn: () => fetchOne<Product>(`/products/${id}`),
    enabled: id > 0,
    ...options,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Product>) => api.post('/products', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<Product> & { id: number }) =>
      api.patch(`/products/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

// ---------------------------------------------------------------------------
// Inventory / Stock Levels
// ---------------------------------------------------------------------------

export function useInventory(params?: PaginationParams & { location?: string; category?: string }) {
  return useQuery({
    queryKey: ['inventory', params],
    queryFn: () => fetchPaginated<StockLevel>('/inventory', params),
  });
}

export function useStockLevels(params?: PaginationParams & { location?: string; category?: string }) {
  return useQuery({
    queryKey: ['stock-levels', params],
    queryFn: () => fetchPaginated<StockLevel>('/inventory/stock-levels', params),
  });
}

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export function useOrders(params?: PaginationParams & { status?: string; customer_id?: number; date_from?: string; date_to?: string }) {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => fetchPaginated<Order>('/orders', params),
  });
}

export function useOrder(id: number) {
  return useQuery({
    queryKey: ['orders', id],
    queryFn: () => fetchOne<Order>(`/orders/${id}`),
    enabled: id > 0,
  });
}

/** Fetch full SO detail from /sales/orders/{id} — includes line items with qty_ordered/qty_picked/qty_shipped */
export interface OrderDetail {
  id: number;
  order_number: string;
  status: string;
  total: number;
  items: OrderDetailLine[];
}

export interface OrderDetailLine {
  id: number;
  product_id: number;
  line_number?: number;
  description: string;
  qty_ordered: number | null;
  qty_picked: number | null;
  qty_shipped: number | null;
  unit_price: number | null;
  extension: number | null;
}

export function useOrderDetail(id: number) {
  return useQuery({
    queryKey: ['order-detail', id],
    queryFn: () => fetchOne<OrderDetail>(`/sales/orders/${id}`),
    enabled: id > 0,
  });
}

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Order>) => api.post('/orders', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

export function useUpdateOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.patch(`/orders/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

// ---------------------------------------------------------------------------
// Purchase Orders
// ---------------------------------------------------------------------------

export function usePurchaseOrders(params?: PaginationParams & { status?: string; vendor_id?: number }) {
  return useQuery({
    queryKey: ['purchase-orders', params],
    queryFn: () => fetchPaginated<PurchaseOrder>('/purchase-orders', params),
  });
}

export function usePO(id: number) {
  return useQuery({
    queryKey: ['purchase-orders', id],
    queryFn: () => fetchOne<PurchaseOrder>(`/purchase-orders/${id}`),
    enabled: id > 0,
  });
}

export function useCreatePO() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<PurchaseOrder>) => api.post('/purchase-orders', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-orders'] }),
  });
}

export function useApprovePO() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'approve' | 'reject' }) =>
      api.post(`/purchase-orders/${id}/${action}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-orders'] }),
  });
}

export function useReceivePO() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, lines }: { id: number; lines: ReceivingLine[] }) =>
      api.post(`/purchase-orders/${id}/receive`, { lines }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['stock-levels'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Vendors
// ---------------------------------------------------------------------------

export function useVendors(params?: PaginationParams & { vendor_type?: string }) {
  return useQuery({
    queryKey: ['vendors', params],
    queryFn: () => fetchPaginated<Vendor>('/vendors', params),
  });
}

export function useVendor(id: number) {
  return useQuery({
    queryKey: ['vendors', id],
    queryFn: () => fetchOne<Vendor>(`/vendors/${id}`),
    enabled: id > 0,
  });
}

export function useCreateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Vendor>) => api.post('/vendors', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  });
}

export function useUpdateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<Vendor> & { id: number }) =>
      api.patch(`/vendors/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  });
}

export function useSubmitPO() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/purchase-orders/${id}/submit`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-orders'] }),
  });
}

export function useCancelPO() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/purchase-orders/${id}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-orders'] }),
  });
}

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export function useCustomers(params?: PaginationParams) {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: () => fetchPaginated<Customer>('/sales/customers', params),
  });
}

export function useCustomer(id: number) {
  return useQuery({
    queryKey: ['customers', id],
    queryFn: () => fetchOne<Customer>(`/sales/customers/${id}`),
    enabled: id > 0,
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Customer>) => api.post('/sales/customers', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<Customer> & { id: number }) =>
      api.patch(`/sales/customers/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useApproveCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/sales/customers/${id}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useRejectCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post(`/sales/customers/${id}/reject`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export interface CustomerOrder {
  id: number;
  order_number: string;
  order_date: string;
  status: string;
  total: number;
}

export interface CustomerInvoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  due_date?: string;
  status: string;
  subtotal?: number;
  tax_amount?: number;
  total: number;
  balance: number;
  terms?: string;
}

export interface CustomerPayment {
  id: number;
  payment_number: string;
  payment_date: string;
  amount: number;
  payment_method?: string;
  method?: string;
  status?: string;
  reference?: string;
}

export function useCustomerOrders(customerId?: number) {
  return useQuery({
    queryKey: ['customer-orders', customerId],
    queryFn: () => fetchPaginated<CustomerOrder>('/sales/orders', { customer_id: customerId }),
    enabled: !!customerId,
  });
}

export function useCustomerInvoices(customerId?: number) {
  return useQuery({
    queryKey: ['customer-invoices', customerId],
    queryFn: () => fetchPaginated<CustomerInvoice>('/sales/invoices', { customer_id: customerId }),
    enabled: !!customerId,
  });
}

export function useCustomerPayments(customerId?: number) {
  return useQuery({
    queryKey: ['customer-payments', customerId],
    queryFn: () => fetchPaginated<CustomerPayment>('/sales/payments', { customer_id: customerId }),
    enabled: !!customerId,
  });
}

// ---------------------------------------------------------------------------
// GL / Accounting
// ---------------------------------------------------------------------------

export function useGLEntries(params?: PaginationParams & { account?: string; date_from?: string; date_to?: string }) {
  return useQuery({
    queryKey: ['gl-entries', params],
    queryFn: () => fetchPaginated<GLEntry>('/accounting/gl-entries', params),
  });
}

export function useTrialBalance(params?: { as_of?: string }) {
  return useQuery({
    queryKey: ['trial-balance', params],
    queryFn: () => fetchOne<TrialBalanceRow[]>('/accounting/trial-balance'),
  });
}

export function usePnL(params?: { period_start?: string; period_end?: string }) {
  return useQuery({
    queryKey: ['pnl', params],
    queryFn: () => fetchOne<PnLRow[]>('/accounting/income-statement'),
  });
}

export function useFinancialReport(reportType: string, params: { period_start: string; period_end: string }, enabled: boolean) {
  return useQuery({
    queryKey: ['financial-report', reportType, params],
    queryFn: async () => {
      const { data } = await api.get<FinancialReport>(`/accounting/reports/${reportType}`, { params });
      return data;
    },
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export function useDashboardKPIs() {
  return useQuery({
    queryKey: ['dashboard-kpis'],
    queryFn: () => fetchOne<DashboardKPIs>('/dashboard/kpis'),
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });
}

// ---------------------------------------------------------------------------
// Home Dashboard KPIs (executive summary from /kpi/* endpoints)
// ---------------------------------------------------------------------------

export interface AgingBuckets {
  current: number;
  '30': number;
  '60': number;
  '90': number;
  '120_plus': number;
}

export interface RevenueKPIs {
  total_revenue: number;
  revenue_per_day: number;
  order_count: number;
  avg_order_value: number;
  revenue_trend: number;
  period_days: number;
}

export interface ARKPIs {
  total_outstanding: number;
  dso: number;
  aging_buckets: AgingBuckets;
  overdue_count: number;
  overdue_amount: number;
}

export interface APKPIs {
  total_outstanding: number;
  dpo: number;
  aging_buckets: AgingBuckets;
  upcoming_due_7d: number;
}

export interface InventoryKPIs {
  total_sku_count: number;
  total_valuation: number;
  low_stock_count: number;
  expiring_soon_count: number;
  spoilage_rate_30d: number;
}

export interface OperationsKPIs {
  orders_shipped: number;
  fill_rate: number;
  on_time_delivery_pct: number;
  avg_pick_to_ship_hours: number;
  period_days: number;
}

export interface HomeDashboardData {
  revenue: RevenueKPIs;
  ar: ARKPIs;
  ap: APKPIs;
  inventory: InventoryKPIs;
  operations: OperationsKPIs;
  generated_at: string;
}

export function useHomeDashboard(days: number = 30) {
  return useQuery({
    queryKey: ['home-dashboard', days],
    queryFn: () => fetchOne<HomeDashboardData>(`/kpi/dashboard?days=${days}`),
    refetchInterval: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Employees / Time
// ---------------------------------------------------------------------------

export function useEmployees(params?: PaginationParams & { department?: string; status?: string }) {
  return useQuery({
    queryKey: ['employees', params],
    queryFn: () => fetchPaginated<Employee>('/employees', params),
  });
}

export function useTimeEntries(params?: PaginationParams & { employee_id?: number; date_from?: string; date_to?: string }) {
  return useQuery({
    queryKey: ['time-entries', params],
    queryFn: () => fetchPaginated<TimeEntry>('/employees/time-entries', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance / Food Safety
// ---------------------------------------------------------------------------

export function useRecalls(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['recalls', params],
    queryFn: () => fetchPaginated<Recall>('/compliance/recalls', params),
  });
}

export function useAudits(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['audits', params],
    queryFn: () => fetchPaginated<Audit>('/compliance/audits', params),
  });
}

// ---------------------------------------------------------------------------
// Users (Admin)
// ---------------------------------------------------------------------------

export function useUsers(params?: PaginationParams) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => fetchPaginated<UserAccount>('/admin/users', params),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<UserAccount> & { password: string }) =>
      api.post('/admin/users', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<UserAccount> & { id: number }) =>
      api.patch(`/admin/users/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      api.post(`/admin/users/${id}/reset-password`, { password }),
  });
}

// ---------------------------------------------------------------------------
// Journal Entries
// ---------------------------------------------------------------------------

export interface JournalEntry {
  id: number;
  entry_number: string;
  date: string;
  description: string;
  status: 'draft' | 'posted' | 'reversed';
  total_debit: number;
  total_credit: number;
  created_by: string;
  lines: JournalEntryLine[];
}

export interface JournalEntryLine {
  account_code: string;
  account_name: string;
  debit: number;
  credit: number;
  memo?: string;
}

export function useJournalEntries(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['journal-entries', params],
    queryFn: () => fetchPaginated<JournalEntry>('/accounting/journal-entries', params),
  });
}

// ---------------------------------------------------------------------------
// Bank Reconciliation
// ---------------------------------------------------------------------------

export interface BankReconciliation {
  id: number;
  account_name: string;
  account_number: string;
  statement_date: string;
  statement_balance: number;
  book_balance: number;
  difference: number;
  status: 'in_progress' | 'completed';
  reconciled_count: number;
  unreconciled_count: number;
}

export function useBankReconciliations(params?: PaginationParams) {
  return useQuery({
    queryKey: ['bank-reconciliations', params],
    queryFn: () => fetchPaginated<BankReconciliation>('/accounting/bank-reconciliations', params),
  });
}

// ---------------------------------------------------------------------------
// AR Credit Memos & Payments
// ---------------------------------------------------------------------------

export interface ARCreditMemo {
  id: number;
  credit_number: string;
  customer_id: number;
  customer_name: string;
  credit_date: string;
  amount: number;
  reason: string;
  status: 'draft' | 'approved' | 'applied';
  invoice_number?: string;
}

export interface ARPayment {
  id: number;
  payment_number: string;
  customer_name: string;
  invoice_number: string;
  payment_date: string;
  amount: number;
  payment_method: string;
  status: 'completed' | 'pending' | 'refunded';
  reference?: string;
}

export function useCreditMemos(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['credit-memos', params],
    queryFn: () => fetchPaginated<ARCreditMemo>('/sales/credit-memos', params),
  });
}

export function useARPayments(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['ar-payments', params],
    queryFn: () => fetchPaginated<ARPayment>('/sales/payments', params),
  });
}

// ---------------------------------------------------------------------------
// Landed Costs & Vendor Catalog
// ---------------------------------------------------------------------------

export interface LandedCost {
  id: number;
  po_number: string;
  vendor_name: string;
  total_product_cost: number;
  freight: number;
  customs: number;
  insurance: number;
  other_costs: number;
  total_landed: number;
  status: 'draft' | 'calculated' | 'applied';
  created_at: string;
}

export interface VendorCatalogItem {
  id: number;
  vendor_id: number;
  vendor_name: string;
  product_name: string;
  vendor_sku: string;
  our_sku: string;
  unit_cost: number;
  min_order_qty: number;
  lead_time_days: number;
  is_preferred: boolean;
}

export function useLandedCosts(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['landed-costs', params],
    queryFn: () => fetchPaginated<LandedCost>('/purchasing/landed-costs', params),
  });
}

export function useCreateLandedCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      receiving_id: number;
      cost_type: string;
      amount: number;
      allocation_method?: string;
      reference?: string;
      notes?: string;
    }) => api.post('/purchasing/landed-costs', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['landed-costs'] }),
  });
}

export function useVendorCatalog(params?: PaginationParams & { vendor_id?: number }) {
  return useQuery({
    queryKey: ['vendor-catalog', params],
    queryFn: () => fetchPaginated<VendorCatalogItem>('/purchasing/vendor-catalog', params),
  });
}

export function useCreateVendorCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<VendorCatalogItem>) =>
      api.post('/purchasing/vendor-items', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-catalog'] }),
  });
}

export function useUpdateVendorCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<VendorCatalogItem> & { id: number }) =>
      api.patch(`/purchasing/vendor-items/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-catalog'] }),
  });
}

// ---------------------------------------------------------------------------
// CRM
// ---------------------------------------------------------------------------

export interface CRMContact {
  id: number;
  customer_id: number;
  customer_name: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  contact_type: string;
  is_primary: boolean;
  is_active: boolean;
}

export interface CRMActivity {
  id: number;
  customer_id: number;
  customer_name: string;
  activity_type: string;
  subject: string;
  details?: string;
  performed_by: string;
  follow_up_date?: string;
  completed: boolean;
  created_at: string;
}

export function useCRMContacts(params?: PaginationParams & { customer_id?: number }) {
  return useQuery({
    queryKey: ['crm-contacts', params],
    queryFn: () => fetchPaginated<CRMContact>('/crm/contacts', params),
  });
}

export function useCRMActivities(params?: PaginationParams & { customer_id?: number; activity_type?: string }) {
  return useQuery({
    queryKey: ['crm-activities', params],
    queryFn: () => fetchPaginated<CRMActivity>('/crm/activities', params),
  });
}

// ---------------------------------------------------------------------------
// Pallets & Expiration Tracking
// ---------------------------------------------------------------------------

export interface Pallet {
  id: number;
  pallet_tag: string;
  product_id: number;
  product_name: string;
  quantity: number;
  lot_number: string;
  location: string;
  status: 'active' | 'in_transit' | 'shipped' | 'empty';
  received_date: string;
  expiry_date?: string;
}

export interface ExpirationItem {
  id: number;
  product_name: string;
  product_sku: string;
  lot_number: string;
  quantity: number;
  location: string;
  expiry_date: string;
  days_until_expiry: number;
  status: 'fresh' | 'expiring_soon' | 'expired';
}

export function usePallets(params?: PaginationParams & { status?: string; location?: string }) {
  return useQuery({
    queryKey: ['pallets', params],
    queryFn: () => fetchPaginated<Pallet>('/inventory/pallets', params),
  });
}

export function useUpdatePallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<Pallet> & { id: number }) =>
      api.patch(`/inventory/pallets/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pallets'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['stock-levels'] });
    },
  });
}

export function useExpirationTracking(params?: PaginationParams & { days_ahead?: number; status?: string }) {
  return useQuery({
    queryKey: ['expiration', params],
    queryFn: () => fetchPaginated<ExpirationItem>('/inventory/expiration-tracking', params),
  });
}

export function useMarkDisposed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api.post(`/inventory/expiration-tracking/${id}/dispose`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expiration'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['stock-levels'] });
    },
  });
}

export function useCreateStockAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { product_id: number; location: string; quantity: number; reason: string }) =>
      api.post('/inventory/adjustments', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-levels'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Compliance: PACA & Cold Chain
// ---------------------------------------------------------------------------

export interface PACARecord {
  id: number;
  transaction_type: string;
  vendor_name: string;
  invoice_number: string;
  commodity: string;
  amount: number;
  trust_eligible: boolean;
  payment_due_date: string;
  days_remaining: number;
  status: 'current' | 'at_risk' | 'overdue';
}

export interface ColdChainRecord {
  id: number;
  location: string;
  sensor_id: string;
  temperature: number;
  humidity: number;
  recorded_at: string;
  status: 'normal' | 'warning' | 'critical';
  min_temp: number;
  max_temp: number;
}

export function usePACARecords(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['paca', params],
    queryFn: () => fetchPaginated<PACARecord>('/compliance/paca', params),
  });
}

export function useColdChainRecords(params?: PaginationParams & { location?: string; status?: string }) {
  return useQuery({
    queryKey: ['cold-chain', params],
    queryFn: () => fetchPaginated<ColdChainRecord>('/compliance/cold-chain', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance: HACCP
// ---------------------------------------------------------------------------

export interface HACCPPlan {
  id: number;
  name: string;
  category: string;
  version: string;
  status: string;
  approved_by: string | null;
  approved_date: string | null;
  created_at: string;
}

export interface HACCPHazard {
  id: number;
  plan_id: number;
  type: string;
  description: string;
  severity: string;
  likelihood: string;
  significant: boolean;
}

export interface CriticalControlPoint {
  id: number;
  plan_id: number;
  ccp_number: string;
  description: string;
  critical_limits: string;
  monitoring_frequency: string;
  responsible_person: string;
}

export interface HACCPVerification {
  id: number;
  plan_id: number;
  verification_type: string;
  scheduled_date: string;
  completed_date: string | null;
  result: string | null;
  verified_by: string | null;
}

export function useHACCPPlans(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['haccp-plans', params],
    queryFn: () => fetchList<HACCPPlan>('/compliance/haccp/plans', params),
  });
}

export function useHACCPHazards(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['haccp-hazards', params],
    queryFn: () => fetchList<HACCPHazard>('/compliance/haccp/hazards', params),
  });
}

export function useCCPs(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['haccp-ccps', params],
    queryFn: () => fetchList<CriticalControlPoint>('/compliance/haccp/ccps', params),
  });
}

export function useHACCPVerifications(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['haccp-verifications', params],
    queryFn: () => fetchList<HACCPVerification>('/compliance/haccp/verifications', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance: GAP / GMP
// ---------------------------------------------------------------------------

export interface GAPInspection {
  id: number;
  inspection_date: string;
  inspector: string;
  inspection_type: string;
  result: string;
  score: number | null;
  next_due_date: string | null;
}

export interface SanitationLog {
  id: number;
  date: string;
  sanitizer: string;
  areas_cleaned: string;
  cleaned_by: string;
  verified_by: string | null;
  meets_standard: boolean;
}

export interface PestControlRecord {
  id: number;
  date: string;
  service_type: string;
  pest_type: string;
  method: string;
  performed_by: string;
  next_service_date: string | null;
}

export interface WaterTestRecord {
  id: number;
  source: string;
  test_type: string;
  test_date: string;
  lab: string;
  result: string;
  next_due_date: string | null;
}

export function useGAPInspections(params?: PaginationParams) {
  return useQuery({
    queryKey: ['gap-inspections', params],
    queryFn: () => fetchPaginated<GAPInspection>('/compliance/gap/inspections', params),
  });
}

export function useGAPSanitation(params?: PaginationParams) {
  return useQuery({
    queryKey: ['gap-sanitation', params],
    queryFn: () => fetchPaginated<SanitationLog>('/compliance/gap/sanitation', params),
  });
}

export function useGAPPestControl(params?: PaginationParams) {
  return useQuery({
    queryKey: ['gap-pest-control', params],
    queryFn: () => fetchPaginated<PestControlRecord>('/compliance/gap/pest-control', params),
  });
}

export function useGAPWaterTests(params?: PaginationParams) {
  return useQuery({
    queryKey: ['gap-water-tests', params],
    queryFn: () => fetchPaginated<WaterTestRecord>('/compliance/gap/water-tests', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance: GFSI Certification
// ---------------------------------------------------------------------------

export interface GFSIAudit {
  id: number;
  scheme: string;
  audit_date: string;
  auditor: string;
  certification_body: string;
  result: string;
  score: number | null;
  grade: string | null;
}

export interface GFSINonConformance {
  id: number;
  audit_id: number;
  nc_number: string;
  category: string;
  clause: string;
  finding: string;
  status: string;
  due_date: string | null;
}

export function useGFSIAudits(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['gfsi-audits', params],
    queryFn: () => fetchList<GFSIAudit>('/compliance/gfsi/audits', params),
  });
}

export function useGFSINonConformances(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['gfsi-ncs', params],
    queryFn: () => fetchList<GFSINonConformance>('/compliance/gfsi/non-conformances', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance: Allergen Control
// ---------------------------------------------------------------------------

export interface AllergenPlan {
  id: number;
  name: string;
  status: string;
  version: string;
  facility_area: string;
  review_due_date: string | null;
}

export interface AllergenOpenRisk {
  id: number;
  source: string;
  target: string;
  allergen: string;
  risk_level: string;
  mitigation: string;
}

export function useAllergenPlans(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['allergen-plans', params],
    queryFn: () => fetchList<AllergenPlan>('/compliance/allergen/plans', params),
  });
}

export function useAllergenOpenRisks(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['allergen-risks', params],
    queryFn: () => fetchList<AllergenOpenRisk>('/compliance/allergen/risks', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance: Environmental Monitoring
// ---------------------------------------------------------------------------

export interface EMPDashboard {
  active_plans: number;
  total_sites: number;
  positive_rate: number;
  overdue_count: number;
}

export interface OverdueSample {
  id: number;
  site_code: string;
  zone: string;
  last_sampled: string;
  frequency: string;
}

export function useEMPDashboard() {
  return useQuery({
    queryKey: ['emp-dashboard'],
    queryFn: () => fetchOne<EMPDashboard>('/compliance/emp/dashboard'),
  });
}

export function useOverdueSamples(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['emp-overdue', params],
    queryFn: () => fetchList<OverdueSample>('/compliance/emp/overdue-samples', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance: Food Defense
// ---------------------------------------------------------------------------

export interface FoodDefensePlan {
  id: number;
  name: string;
  facility: string;
  plan_type: string;
  status: string;
  effective_date: string | null;
}

export interface FDRiskDashboard {
  active_plans: number;
  high_risk_assessments: number;
  open_incidents: number;
  overdue_trainings: number;
}

export interface FDTraining {
  id: number;
  employee_name: string;
  training_type: string;
  expiry_date: string;
}

export function useFDPlans(params?: PaginationParams) {
  return useQuery({
    queryKey: ['fd-plans', params],
    queryFn: () => fetchPaginated<FoodDefensePlan>('/compliance/food-defense/plans', params),
  });
}

export function useFDRiskDashboard() {
  return useQuery({
    queryKey: ['fd-risk-dashboard'],
    queryFn: () => fetchOne<FDRiskDashboard>('/compliance/food-defense/dashboard'),
  });
}

export function useFDOverdueTrainings(params?: PaginationParams) {
  return useQuery({
    queryKey: ['fd-trainings', params],
    queryFn: () =>
      fetchPaginated<FDTraining>('/compliance/food-defense/overdue-trainings', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance: FSMA Preventive Controls
// ---------------------------------------------------------------------------

export interface FSMAPlan {
  id: number;
  name: string;
  version: string;
  status: string;
  effective_date: string | null;
  has_recall_plan: boolean;
}

export interface FSMAOverdueVerification {
  id: number;
  plan_id: number;
  verification_type: string;
  scheduled_date: string;
}

export function useFSMAPlans(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['fsma-plans', params],
    queryFn: () => fetchList<FSMAPlan>('/compliance/fsma/plans', params),
  });
}

export function useFSMAOverdueVerifications(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['fsma-overdue', params],
    queryFn: () =>
      fetchList<FSMAOverdueVerification>('/compliance/fsma/overdue-verifications', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance: Organic Chain of Custody
// ---------------------------------------------------------------------------

export interface OrganicCertification {
  id: number;
  entity: string;
  certifier: string;
  certificate_number: string;
  valid_from: string;
  valid_to: string;
  status: string;
}

export interface OrganicTransaction {
  id: number;
  transaction_type: string;
  lot_number: string;
  quantity: number;
  unit: string;
  date: string;
}

export interface OrganicMassBalance {
  id: number;
  product: string;
  period: string;
  opening_balance: number;
  received: number;
  sold: number;
  closing_balance: number;
  status: string;
}

export interface OrganicLabelClaim {
  id: number;
  lot_number: string;
  claim_type: string;
  verified: boolean;
  date: string;
}

export function useOrganicCertifications(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['organic-certs', params],
    queryFn: () =>
      fetchList<OrganicCertification>('/compliance/organic/certifications', params),
  });
}

export function useOrganicTransactions(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['organic-transactions', params],
    queryFn: () =>
      fetchList<OrganicTransaction>('/compliance/organic/transactions', params),
  });
}

export function useOrganicMassBalances(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['organic-mass-balances', params],
    queryFn: () =>
      fetchList<OrganicMassBalance>('/compliance/organic/mass-balances', params),
  });
}

export function useOrganicLabelClaims(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['organic-label-claims', params],
    queryFn: () =>
      fetchList<OrganicLabelClaim>('/compliance/organic/label-claims', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance: Pesticide & MRL
// ---------------------------------------------------------------------------

export interface PesticideLabResult {
  id: number;
  lot_number: string;
  lab: string;
  test_date: string;
  commodity: string;
  status: string;
}

export interface PesticideCompound {
  id: number;
  name: string;
  cas_number: string;
  chemical_group: string;
  is_active: boolean;
}

export interface MRLStandard {
  id: number;
  compound: string;
  commodity: string;
  market: string;
  mrl_ppm: number;
  effective_date: string;
}

export interface COACertificate {
  id: number;
  certificate_number: string;
  lot_number: string;
  issued_date: string;
  status: string;
}

export function usePesticideLabResults(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['pesticide-results', params],
    queryFn: () =>
      fetchList<PesticideLabResult>('/compliance/pesticide/lab-results', params),
  });
}

export function usePesticideCompounds(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['pesticide-compounds', params],
    queryFn: () =>
      fetchList<PesticideCompound>('/compliance/pesticide/compounds', params),
  });
}

export function useMRLStandards(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['mrl-standards', params],
    queryFn: () => fetchList<MRLStandard>('/compliance/pesticide/mrl-standards', params),
  });
}

export function useCOACertificates(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['coa-certificates', params],
    queryFn: () =>
      fetchList<COACertificate>('/compliance/pesticide/coa-certificates', params),
  });
}

// ---------------------------------------------------------------------------
// Compliance: LGMA
// ---------------------------------------------------------------------------

export interface LGMACertification {
  id: number;
  facility: string;
  certificate_number: string;
  status: string;
  issued_date: string;
  expiry_date: string;
  certifying_body: string;
  score: number | null;
  commodities: string;
}

export interface LGMADashboard {
  active_certs: number;
  avg_score: number;
  expiring_soon: number;
}

export function useLGMACerts(params?: PaginationParams) {
  return useQuery({
    queryKey: ['lgma-certs', params],
    queryFn: () => fetchPaginated<LGMACertification>('/compliance/lgma/certifications', params),
  });
}

export function useLGMADashboard() {
  return useQuery({
    queryKey: ['lgma-dashboard'],
    queryFn: () => fetchOne<LGMADashboard>('/compliance/lgma/dashboard'),
  });
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

export interface PriceRule {
  id: number;
  name: string;
  product_name?: string;
  customer_name?: string;
  pricing_tier: string;
  base_price: number;
  adjusted_price: number;
  discount_pct: number;
  effective_date: string;
  expiry_date?: string;
  is_active: boolean;
}

export function usePriceRules(params?: PaginationParams & { tier?: string; active_only?: boolean }) {
  return useQuery({
    queryKey: ['price-rules', params],
    queryFn: () => fetchPaginated<PriceRule>('/pricing/rules', params),
  });
}

// ---------------------------------------------------------------------------
// Forecasting
// ---------------------------------------------------------------------------

export interface ForecastItem {
  id: number;
  product_name: string;
  product_sku: string;
  period: string;
  forecast_qty: number;
  actual_qty?: number;
  variance_pct?: number;
  method: string;
  confidence: number;
}

export function useForecasts(params?: PaginationParams & { method?: string; period?: string }) {
  return useQuery({
    queryKey: ['forecasts', params],
    queryFn: () => fetchPaginated<ForecastItem>('/forecasting/forecasts', params),
  });
}

// ---------------------------------------------------------------------------
// Admin: Roles & Permissions
// ---------------------------------------------------------------------------

export interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  is_system: boolean;
  user_count: number;
  permissions: string[];
  created_at: string;
  updated_at: string;
}

export function useRoles(params?: PaginationParams) {
  return useQuery({
    queryKey: ['roles', params],
    queryFn: () => fetchPaginated<Role>('/admin/roles', params),
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Role>) => api.post('/admin/roles', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<Role> & { id: number }) =>
      api.patch(`/admin/roles/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/admin/roles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}

// ---------------------------------------------------------------------------
// Admin: Audit Log
// ---------------------------------------------------------------------------

export interface AuditLogEntry {
  id: number;
  timestamp: string;
  username: string;
  action: string;
  module: string;
  resource_type: string;
  resource_id: string;
  details: string;
  before_value?: Record<string, unknown>;
  after_value?: Record<string, unknown>;
  ip_address: string;
}

export function useAuditLog(params?: PaginationParams & {
  username?: string;
  module?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
}) {
  return useQuery({
    queryKey: ['audit-log', params],
    queryFn: () => fetchPaginated<AuditLogEntry>('/admin/audit-log', params),
  });
}

// ---------------------------------------------------------------------------
// Admin: System Settings
// ---------------------------------------------------------------------------

export interface SystemSettings {
  company_name: string;
  company_address: string;
  company_phone: string;
  company_email: string;
  tax_rate: number;
  tax_id: string;
  default_payment_terms: string;
  default_currency: string;
  fiscal_year_start: string;
  notification_email_enabled: boolean;
  notification_low_stock: boolean;
  notification_order_updates: boolean;
  notification_compliance_alerts: boolean;
  integration_quickbooks_enabled: boolean;
  integration_shopify_enabled: boolean;
  integration_edi_enabled: boolean;
}

export function useSystemSettings() {
  return useQuery({
    queryKey: ['system-settings'],
    queryFn: () => fetchOne<SystemSettings>('/admin/settings'),
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<SystemSettings>) =>
      api.put('/admin/settings', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system-settings'] }),
  });
}

// ---------------------------------------------------------------------------
// Admin: API Tokens
// ---------------------------------------------------------------------------

export interface ApiToken {
  id: number;
  name: string;
  token_prefix: string;
  scopes: string[];
  created_by: string;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  request_count: number;
  is_active: boolean;
}

export function useApiTokens(params?: PaginationParams) {
  return useQuery({
    queryKey: ['api-tokens', params],
    queryFn: () => fetchPaginated<ApiToken>('/admin/api-tokens', params),
  });
}

export function useCreateApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; scopes: string[]; expires_in_days?: number }) =>
      api.post<{ token: string; id: number }>('/admin/api-tokens', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-tokens'] }),
  });
}

export function useRevokeApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/admin/api-tokens/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-tokens'] }),
  });
}

// ---------------------------------------------------------------------------
// Admin: Webhooks
// ---------------------------------------------------------------------------

export interface Webhook {
  id: number;
  name: string;
  url: string;
  events: string[];
  is_active: boolean;
  secret: string;
  retry_count: number;
  timeout_seconds: number;
  created_at: string;
  last_triggered_at: string | null;
  success_count: number;
  failure_count: number;
}

export interface WebhookDelivery {
  id: number;
  webhook_id: number;
  event: string;
  status_code: number | null;
  success: boolean;
  request_body: string;
  response_body: string | null;
  attempted_at: string;
  duration_ms: number;
}

export function useWebhooks(params?: PaginationParams) {
  return useQuery({
    queryKey: ['webhooks', params],
    queryFn: () => fetchPaginated<Webhook>('/admin/webhooks', params),
  });
}

export function useWebhookDeliveries(webhookId: number, params?: PaginationParams) {
  return useQuery({
    queryKey: ['webhook-deliveries', webhookId, params],
    queryFn: () => fetchPaginated<WebhookDelivery>(`/admin/webhooks/${webhookId}/deliveries`, params),
    enabled: webhookId > 0,
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Webhook>) => api.post('/admin/webhooks', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}

export function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<Webhook> & { id: number }) =>
      api.patch(`/admin/webhooks/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/admin/webhooks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}

// ---------------------------------------------------------------------------
// Admin: Tenants
// ---------------------------------------------------------------------------

export interface Tenant {
  id: number;
  name: string;
  slug: string;
  domain: string;
  status: 'active' | 'inactive' | 'suspended';
  plan: string;
  user_count: number;
  data_isolation: 'isolated' | 'shared';
  billing_status: 'current' | 'past_due' | 'trial' | 'cancelled';
  created_at: string;
  storage_used_mb: number;
  contact_email: string;
}

export function useTenants(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['tenants', params],
    queryFn: () => fetchPaginated<Tenant>('/admin/tenants', params),
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Tenant>) => api.post('/admin/tenants', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<Tenant> & { id: number }) =>
      api.patch(`/admin/tenants/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });
}

// ---------------------------------------------------------------------------
// Operations: Cold Chain Zones & Readings
// ---------------------------------------------------------------------------

export interface ColdChainZone {
  id: number;
  zone_name: string;
  zone_type: string;
  current_temp: number;
  target_temp: number;
  min_temp: number;
  max_temp: number;
  humidity: number;
  sensor_count: number;
  compliance_pct: number;
  last_reading: string;
  status: 'normal' | 'warning' | 'critical';
}

export interface ColdChainReading {
  id: number;
  zone_id: number;
  timestamp: string;
  temperature: number;
  humidity: number;
}

export function useColdChainZones() {
  return useQuery({
    queryKey: ['cold-chain-zones'],
    queryFn: async () => {
      const { data } = await api.get<ColdChainZone[]>('/operations/cold-chain/zones');
      return Array.isArray(data) ? data : [];
    },
    refetchInterval: 60 * 1000,
  });
}

export function useColdChainReadings(zoneId: number, hours: number) {
  return useQuery({
    queryKey: ['cold-chain-readings', zoneId, hours],
    queryFn: async () => {
      const { data } = await api.get<ColdChainReading[]>(
        `/operations/cold-chain/zones/${zoneId}/readings`,
        { params: { hours } },
      );
      return Array.isArray(data) ? data : [];
    },
    enabled: zoneId > 0,
  });
}

// ---------------------------------------------------------------------------
// Operations: Cycle Counts
// ---------------------------------------------------------------------------

export interface CycleCount {
  id: number;
  count_number: string;
  location: string;
  zone: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  scheduled_date: string | null;
  counter: string | null;
  total_items: number;
  counted_items: number;
  variance_count: number;
  variance_value: number;
  notes?: string;
  created_at: string;
}

export function useCycleCounts(params?: PaginationParams & { status?: string; zone?: string }) {
  return useQuery({
    queryKey: ['cycle-counts', params],
    queryFn: () => fetchPaginated<CycleCount>('/operations/cycle-counts', params),
  });
}

export function useCreateCycleCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<CycleCount>) =>
      api.post('/operations/cycle-counts', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cycle-counts'] }),
  });
}

export function useUpdateCycleCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<CycleCount> & { id: number }) =>
      api.patch(`/operations/cycle-counts/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cycle-counts'] }),
  });
}

// ---------------------------------------------------------------------------
// Operations: Pick Lists
// ---------------------------------------------------------------------------

export interface PickList {
  id: number;
  pick_number: string;
  order_number: string;
  customer_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: string;
  pick_method: string;
  assignee: string | null;
  total_items: number;
  picked_items: number;
  notes?: string;
  created_at: string;
}

export function usePickLists(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['pick-lists', params],
    queryFn: () => fetchPaginated<PickList>('/operations/pick-lists', params),
  });
}

export function useCreatePickList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<PickList>) =>
      api.post('/operations/pick-lists', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pick-lists'] }),
  });
}

export function useUpdatePickList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<PickList> & { id: number }) =>
      api.patch(`/operations/pick-lists/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pick-lists'] }),
  });
}

// ---------------------------------------------------------------------------
// Operations: Inbound Receiving
// ---------------------------------------------------------------------------

export interface InboundReceipt {
  id: number;
  receipt_number: string;
  po_number: string;
  vendor_name: string;
  carrier: string;
  status: 'scheduled' | 'arrived' | 'inspecting' | 'receiving' | 'completed' | 'rejected';
  inspection_status: string;
  dock_door: string | null;
  seal_number: string | null;
  temperature_on_arrival: number | null;
  total_items: number;
  received_items: number;
  scheduled_date: string;
  arrival_date: string | null;
  notes?: string;
  created_at: string;
}

export function useInboundReceipts(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['inbound-receipts', params],
    queryFn: () => fetchPaginated<InboundReceipt>('/operations/receiving', params),
  });
}

export function useCreateInboundReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post('/operations/receiving', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbound-receipts'] }),
  });
}

export function useUpdateInboundReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: number } & Record<string, unknown>) =>
      api.patch(`/operations/receiving/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbound-receipts'] }),
  });
}

// ---------------------------------------------------------------------------
// Operations: Delivery Routes
// ---------------------------------------------------------------------------

export interface DeliveryRouteStop {
  id: number;
  sequence: number;
  customer_name: string;
  address: string;
  order_number: string;
  estimated_arrival: string;
  actual_arrival?: string;
  status: 'pending' | 'arrived' | 'delivered' | 'skipped';
}

export interface DeliveryRoute {
  id: number;
  route_number: string;
  driver: string;
  vehicle: string;
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  date: string;
  total_stops: number;
  completed_stops: number;
  total_distance: number;
  estimated_duration: number;
  actual_duration?: number;
  stops?: DeliveryRouteStop[];
  notes?: string;
  created_at: string;
}

export function useDeliveryRoutes(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['delivery-routes', params],
    queryFn: () => fetchPaginated<DeliveryRoute>('/operations/routes', params),
  });
}

export function useCreateRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post('/operations/routes', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-routes'] }),
  });
}

export function useUpdateRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: number } & Record<string, unknown>) =>
      api.patch(`/operations/routes/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-routes'] }),
  });
}

// ---------------------------------------------------------------------------
// Operations: Scheduling / Shifts
// ---------------------------------------------------------------------------

export interface Shift {
  id: number;
  employee_name: string;
  department: string;
  shift_date: string;
  start_time: string;
  end_time: string;
  shift_type: string;
  hours: number;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  notes?: string;
  created_at: string;
}

export function useShifts(params?: PaginationParams & {
  department?: string;
  date_from?: string;
  date_to?: string;
}) {
  return useQuery({
    queryKey: ['shifts', params],
    queryFn: () => fetchPaginated<Shift>('/operations/shifts', params),
  });
}

export function useCreateShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post('/operations/shifts', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shifts'] }),
  });
}

export function useUpdateShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: number } & Record<string, unknown>) =>
      api.patch(`/operations/shifts/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shifts'] }),
  });
}

// ---------------------------------------------------------------------------
// Operations: Shipments
// ---------------------------------------------------------------------------

export interface Shipment {
  id: number;
  shipment_number: string;
  order_number: string;
  customer_name: string;
  status: 'pending' | 'loading' | 'shipped' | 'in_transit' | 'delivered' | 'cancelled';
  carrier: string;
  dock_door: string | null;
  tracking_number: string | null;
  bol_number: string | null;
  weight: number;
  pallet_count: number;
  ship_date: string | null;
  estimated_delivery: string | null;
  notes?: string;
  created_at: string;
}

export function useShipments(params?: PaginationParams & { status?: string; carrier?: string }) {
  return useQuery({
    queryKey: ['shipments', params],
    queryFn: () => fetchPaginated<Shipment>('/operations/shipments', params),
  });
}

export function useCreateShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post('/operations/shipments', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments'] }),
  });
}

export function useUpdateShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: number } & Record<string, unknown>) =>
      api.patch(`/operations/shipments/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments'] }),
  });
}

// ---------------------------------------------------------------------------
// Exception Queue
// ---------------------------------------------------------------------------

export interface ExceptionItem {
  id: number;
  exception_type: string;
  source_type: string | null;
  source_id: number | null;
  severity: 'critical' | 'warning' | 'info';
  status: 'open' | 'acknowledged' | 'resolved' | 'dismissed';
  title: string;
  description: string | null;
  assigned_to: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  tenant_id: string | null;
  created_by: string;
  created_at: string | null;
}

export interface ExceptionSummary {
  total_open: number;
  by_severity: { critical: number; warning: number; info: number };
  by_type: Record<string, number>;
  by_status: { open: number; acknowledged: number; resolved: number; dismissed: number };
}

export interface AutoApproveConfig {
  id: number;
  config_key: string;
  max_variance_pct: number | null;
  max_amount: number | null;
  is_enabled: boolean;
  tenant_id: string | null;
}

export function useExceptions(params?: {
  status?: string;
  severity?: string;
  exception_type?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery<{ items: ExceptionItem[]; total: number; limit: number; offset: number }>({
    queryKey: ['exceptions', params],
    queryFn: async () => {
      const res = await api.get('/exceptions/', { params });
      return res.data;
    },
  });
}

export function useExceptionSummary() {
  return useQuery<ExceptionSummary>({
    queryKey: ['exception-summary'],
    queryFn: async () => {
      const res = await api.get('/exceptions/summary');
      return res.data;
    },
  });
}

export function useAcknowledgeException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.post(`/exceptions/${id}/acknowledge`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exceptions'] });
      qc.invalidateQueries({ queryKey: ['exception-summary'] });
    },
  });
}

export function useResolveException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes: string }) => {
      const res = await api.post(`/exceptions/${id}/resolve`, { notes });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exceptions'] });
      qc.invalidateQueries({ queryKey: ['exception-summary'] });
    },
  });
}

export function useDismissException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes: string }) => {
      const res = await api.post(`/exceptions/${id}/dismiss`, { notes });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exceptions'] });
      qc.invalidateQueries({ queryKey: ['exception-summary'] });
    },
  });
}

export function useAutoApproveConfigs() {
  return useQuery<AutoApproveConfig[]>({
    queryKey: ['auto-approve-configs'],
    queryFn: async () => {
      const res = await api.get('/exceptions/config/auto-approve');
      return res.data;
    },
  });
}

export function useUpsertAutoApproveConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      config_key: string;
      max_variance_pct: number | null;
      max_amount: number | null;
      is_enabled: boolean;
    }) => {
      const res = await api.post('/exceptions/config/auto-approve', payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auto-approve-configs'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Journal Entry Approval Workflow (Full)
// ---------------------------------------------------------------------------

export interface JournalEntryFull {
  id: number;
  entry_number: string;
  entry_date: string;
  memo: string;
  source: string;
  is_posted: boolean;
  approval_status: 'draft' | 'pending_review' | 'approved' | 'posted' | 'rejected' | 'auto_approved';
  submitted_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
  approval_level_required: string | null;
  fiscal_period: string | null;
  currency_code: string;
  exchange_rate: number | null;
  is_reversing: boolean;
  reversed_by: number | null;
  created_by: string;
  created_at: string | null;
  total_debit: number;
  total_credit: number;
  line_count: number;
  lines: JournalLineFull[];
}

export interface JournalLineFull {
  id: number;
  account_number: string;
  debit: number;
  credit: number;
  memo: string;
  department: string | null;
  project: string | null;
  currency_code?: string;
  foreign_amount?: number | null;
  exchange_rate?: number | null;
}

export function useJournalEntriesFull(params?: PaginationParams & {
  approval_status?: string;
  source?: string;
  date_from?: string;
  date_to?: string;
}) {
  return useQuery({
    queryKey: ['journal-entries-full', params],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<JournalEntryFull>>('/accounting/gl/entries', { params });
      return res.data;
    },
  });
}

export function useJournalEntryDetail(id: number) {
  return useQuery({
    queryKey: ['journal-entry-detail', id],
    queryFn: async () => {
      const res = await api.get<JournalEntryFull>(`/accounting/gl/entries/${id}`);
      return res.data;
    },
    enabled: id > 0,
  });
}

export function useCreateDraftJE() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      entry_date?: string;
      memo: string;
      lines: { account_number: string; debit: number; credit: number; memo?: string }[];
      reference_type?: string;
      reference_id?: string;
    }) => {
      const res = await api.post('/accounting/gl/draft-journal-entry', payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['journal-entries-full'] });
      qc.invalidateQueries({ queryKey: ['journal-entries'] });
    },
  });
}

export function useSubmitJE() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: number) => {
      const res = await api.post(`/accounting/gl/entries/${entryId}/submit`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['journal-entries-full'] });
      qc.invalidateQueries({ queryKey: ['journal-entries'] });
    },
  });
}

export function useApproveJE() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, approver_role }: { id: number; approver_role?: string }) => {
      const res = await api.post(`/accounting/gl/entries/${id}/approve`, { approver_role });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['journal-entries-full'] });
      qc.invalidateQueries({ queryKey: ['journal-entries'] });
    },
  });
}

export function useRejectJE() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => {
      const res = await api.post(`/accounting/gl/entries/${id}/reject`, { reason });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['journal-entries-full'] });
      qc.invalidateQueries({ queryKey: ['journal-entries'] });
    },
  });
}

export function useReverseJE() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reversal_date }: { id: number; reversal_date?: string }) => {
      const res = await api.post(`/accounting/gl/journal-entry/${id}/reverse`, { reversal_date });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['journal-entries-full'] });
      qc.invalidateQueries({ queryKey: ['journal-entries'] });
      qc.invalidateQueries({ queryKey: ['gl-entries'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Fixed Assets
// ---------------------------------------------------------------------------

export interface FixedAsset {
  id: number;
  name: string;
  status: 'active' | 'fully_depreciated' | 'disposed';
  category: string;
  purchase_cost: number;
  useful_life_months: number;
  salvage_value: number;
  depreciation_method: string;
  accumulated_depreciation: number;
  book_value: number;
  location: string | null;
}

export function useFixedAssets(params?: PaginationParams) {
  return useQuery({
    queryKey: ['fixed-assets', params],
    queryFn: () => fetchPaginated<FixedAsset>('/accounting/assets', params),
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      asset_number: string;
      name: string;
      category?: string;
      acquisition_date?: string;
      acquisition_cost: number;
      useful_life_months: number;
      salvage_value?: number;
      depreciation_method?: string;
      location?: string;
    }) => api.post('/accounting/assets', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fixed-assets'] }),
  });
}

export function useDepreciateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: number) => api.post(`/accounting/assets/${assetId}/depreciate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fixed-assets'] }),
  });
}

export function useDepreciateAllAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/accounting/assets/depreciate-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fixed-assets'] }),
  });
}

export function useDisposeAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: number; disposal_date?: string; proceeds?: number }) =>
      api.post(`/accounting/assets/${id}/dispose`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fixed-assets'] }),
  });
}

// ---------------------------------------------------------------------------
// Budgets
// ---------------------------------------------------------------------------

export interface BudgetItem {
  id: number;
  name: string;
  fiscal_year: number;
  status: 'draft' | 'active' | 'closed';
}

export interface BudgetVariance {
  account_number: string;
  account_name: string;
  budgeted: number;
  actual: number;
  variance: number;
  variance_pct: number;
}

export function useBudgets(params?: PaginationParams & { fiscal_year?: number }) {
  return useQuery({
    queryKey: ['budgets', params],
    queryFn: () => fetchPaginated<BudgetItem>('/accounting/budgets', params),
  });
}

export function useCreateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; fiscal_year: number; lines?: { account_number: string; amount: number }[] }) =>
      api.post('/accounting/budgets', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budgets'] }),
  });
}

export function useActivateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (budgetId: number) => api.post(`/accounting/budgets/${budgetId}/activate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budgets'] }),
  });
}

export function useCloseBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (budgetId: number) => api.post(`/accounting/budgets/${budgetId}/close`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budgets'] }),
  });
}

export function useBudgetVariance(budgetId: number) {
  return useQuery({
    queryKey: ['budget-variance', budgetId],
    queryFn: async () => {
      const res = await api.get<BudgetVariance[]>(`/accounting/budgets/${budgetId}/variance`);
      return res.data;
    },
    enabled: budgetId > 0,
  });
}

// ---------------------------------------------------------------------------
// Currency / Exchange Rates
// ---------------------------------------------------------------------------

export interface CurrencyRate {
  from: string;
  to: string;
  rate: number;
  effective: string;
}

export function useCurrencyRates(fromCurrency?: string) {
  return useQuery({
    queryKey: ['currency-rates', fromCurrency],
    queryFn: async () => {
      const params = fromCurrency ? { from_currency: fromCurrency } : {};
      const res = await api.get<CurrencyRate[]>('/accounting/currency/rates', { params });
      return res.data;
    },
  });
}

export function useSetCurrencyRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      from_currency: string;
      to_currency: string;
      rate: number;
      effective_date?: string;
    }) => api.post('/accounting/currency/rates', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['currency-rates'] }),
  });
}

export function useConvertCurrency() {
  return useMutation({
    mutationFn: async (payload: { amount: number; from_currency: string; to_currency: string }) => {
      const res = await api.post('/accounting/currency/convert', payload);
      return res.data as { converted_amount: number; from: string; to: string };
    },
  });
}

// ---------------------------------------------------------------------------
// Fiscal Periods
// ---------------------------------------------------------------------------

export interface FiscalPeriod {
  id: number;
  period: string;
  fiscal_year: number;
  period_month: number;
  status: string;
  is_locked: boolean;
  locked_at: string | null;
  locked_by: string | null;
  closed_at: string | null;
}

export function useFiscalPeriods(params?: PaginationParams & { fiscal_year?: number }) {
  return useQuery({
    queryKey: ['fiscal-periods', params],
    queryFn: () => fetchPaginated<FiscalPeriod>('/accounting/periods', params),
  });
}

export function useCloseFiscalPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) => api.post('/accounting/periods/close', { period }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fiscal-periods'] }),
  });
}

export function useUnlockFiscalPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ period, password, reason }: { period: string; password: string; reason: string }) =>
      api.post(`/accounting/admin/periods/${period}/unlock`, { password, reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fiscal-periods'] }),
  });
}

export function useYearEndClose() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fiscal_year: number) => api.post('/accounting/periods/year-end-close', { fiscal_year }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fiscal-periods'] });
      qc.invalidateQueries({ queryKey: ['journal-entries-full'] });
      qc.invalidateQueries({ queryKey: ['gl-entries'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Tax Compliance
// ---------------------------------------------------------------------------

export interface ExpiringW9Vendor {
  vendor_id: number;
  vendor_name: string;
  w9_expiration_date: string;
  days_until_expiry: number;
}

export function useExpiringW9Vendors(daysAhead: number = 90) {
  return useQuery({
    queryKey: ['expiring-w9', daysAhead],
    queryFn: async () => {
      const res = await api.get<ExpiringW9Vendor[]>('/accounting/tax/vendors/expiring-w9', {
        params: { days_ahead: daysAhead },
      });
      return res.data;
    },
  });
}

export function use1099Readiness(taxYear: number) {
  return useQuery({
    queryKey: ['1099-readiness', taxYear],
    queryFn: async () => {
      const res = await api.get(`/accounting/tax/1099/readiness/${taxYear}`);
      return res.data;
    },
    enabled: taxYear > 0,
  });
}

// ---------------------------------------------------------------------------
// Finance: Petty Cash
// ---------------------------------------------------------------------------
export function usePettyCashBoxes(params?: Record<string, any>) {
  return useQuery({ queryKey: ['petty-cash-boxes', params], queryFn: () => fetchList<any>('/petty-cash', params) });
}
export function usePettyCashVouchers(params?: Record<string, any>) {
  return useQuery({ queryKey: ['petty-cash-vouchers', params], queryFn: () => fetchList<any>('/petty-cash/vouchers', params) });
}
export function useCreateVoucher() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (payload: Record<string, unknown>) => api.post('/petty-cash/vouchers', payload), onSuccess: () => qc.invalidateQueries({ queryKey: ['petty-cash-vouchers'] }) });
}
export function useApproveVoucher() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: number) => api.post(`/petty-cash/vouchers/${id}/approve`), onSuccess: () => qc.invalidateQueries({ queryKey: ['petty-cash-vouchers'] }) });
}
export function useReplenishBox() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (payload: { box_id: number; amount: number }) => api.post('/petty-cash/replenish', payload), onSuccess: () => qc.invalidateQueries({ queryKey: ['petty-cash-boxes'] }) });
}

// Finance: Intercompany
export function useIntercompanyTransactions(params?: Record<string, any>) {
  return useQuery({ queryKey: ['intercompany-transactions', params], queryFn: () => fetchList<any>('/intercompany/agreements', params) });
}
export function useIntercompanyEliminations(params?: Record<string, any>) {
  return useQuery({ queryKey: ['intercompany-eliminations', params], queryFn: () => fetchList<any>('/intercompany/elimination-entries', params) });
}

// Finance: Financial Close
export function useCloseChecklists(params?: Record<string, any>) {
  return useQuery({ queryKey: ['close-checklists', params], queryFn: () => fetchList<any>('/finance/close/templates', params) });
}
export function useClosePeriods(params?: Record<string, any>) {
  return useQuery({ queryKey: ['close-periods', params], queryFn: () => fetchList<any>('/finance/close/reconciliations', params) });
}

// Finance: Lease Accounting
export function useLeases(params?: Record<string, any>) {
  return useQuery({ queryKey: ['leases', params], queryFn: () => fetchList<any>('/lease-accounting/leases', params) });
}
export function useLeasePayments(params?: Record<string, any>) {
  return useQuery({ queryKey: ['lease-payments', params], queryFn: () => fetchList<any>('/lease-accounting/lease_payment_schedules', params) });
}

// Finance: Cash Application
export function useCashMatches(params?: Record<string, any>) {
  return useQuery({ queryKey: ['cash-matches', params], queryFn: () => fetchList<any>('/cash-application/batches', params) });
}
export function useUnmatchedPayments(params?: Record<string, any>) {
  return useQuery({ queryKey: ['unmatched-payments', params], queryFn: () => fetchList<any>('/cash-application/unmatched', params) });
}

// Finance: Tax
export function useConsolidatedTaxEntities(params?: Record<string, any>) {
  return useQuery({ queryKey: ['consolidated-tax', params], queryFn: () => fetchList<any>('/consolidated-tax/groups', params) });
}
export function useDeferredTaxAssets(params?: Record<string, any>) {
  return useQuery({ queryKey: ['deferred-tax', params], queryFn: () => fetchList<any>('/deferred-tax/temporary-differences', params) });
}

// Finance: Cost Centers
export function useCostCenters(params?: Record<string, any>) {
  return useQuery({ queryKey: ['cost-centers', params], queryFn: () => fetchList<any>('/cost-centers', params) });
}
export function useCreateCostCenter() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (payload: Record<string, unknown>) => api.post('/cost-centers', payload), onSuccess: () => qc.invalidateQueries({ queryKey: ['cost-centers'] }) });
}

// Finance: Deferred Revenue
export function useDeferredRevenue(params?: Record<string, any>) {
  return useQuery({ queryKey: ['deferred-revenue', params], queryFn: () => fetchList<any>('/revenue/contracts', params) });
}

// Finance: Bad Debt
export function useBadDebtWriteoffs(params?: Record<string, any>) {
  return useQuery({ queryKey: ['bad-debt', params], queryFn: () => fetchList<any>('/accounting/bad-debt/writeoffs', params) });
}

// Finance: AP Aging
export function useAPAgingReport(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ap-aging', params], queryFn: () => fetchOne<any>('/ap-aging/snapshots') });
}

// Finance: AP Duplicates
export function useAPDuplicates(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ap-duplicates', params], queryFn: () => fetchList<any>('/ap-duplicates/checks', params) });
}

// ---------------------------------------------------------------------------
// AP Invoices
// ---------------------------------------------------------------------------

export interface APInvoiceRecord {
  id: number;
  invoice_number: string;
  vendor_id: number;
  vendor_name?: string;
  vendor_invoice?: string;
  status: string;
  invoice_date: string;
  due_date: string;
  terms?: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  balance: number;
  notes?: string;
  approved_by?: string;
  approved_at?: string;
  gl_posted?: boolean;
  currency_code?: string;
}

export function useAPInvoices(params?: PaginationParams & { status?: string; vendor_id?: number }) {
  return useQuery({
    queryKey: ['ap-invoices', params],
    queryFn: async () => {
      const res = await api.get('/ap/invoices', { params });
      return res.data as { items: APInvoiceRecord[]; total: number; offset: number; limit: number };
    },
  });
}

export function useAPInvoiceDetail(id: number) {
  return useQuery({
    queryKey: ['ap-invoice-detail', id],
    queryFn: async () => {
      const res = await api.get(`/ap-invoices/${id}/summary`);
      return res.data;
    },
    enabled: id > 0,
  });
}

// Finance: Bank Import
export function useBankImports(params?: Record<string, any>) {
  return useQuery({ queryKey: ['bank-imports', params], queryFn: () => fetchList<any>('/banking/import/statements', params) });
}

// Finance: Plaid
export function usePlaidConnections(params?: Record<string, any>) {
  return useQuery({ queryKey: ['plaid-connections', params], queryFn: () => fetchList<any>('/plaid/connections', params) });
}
export function usePlaidTransactions(params?: Record<string, any>) {
  return useQuery({ queryKey: ['plaid-transactions', params], queryFn: () => fetchList<any>('/plaid/transactions', params) });
}

// Finance: Check/Wire
export function useCheckWirePayments(params?: Record<string, any>) {
  return useQuery({ queryKey: ['check-wire', params], queryFn: () => fetchList<any>('/banking/payments/checks/batches', params) });
}

// Finance: Payment Links
export function usePaymentLinks(params?: Record<string, any>) {
  return useQuery({ queryKey: ['payment-links', params], queryFn: () => fetchList<any>('/payment-links', params) });
}

// Finance: Payment Orchestrator
export function usePaymentRouting(params?: Record<string, any>) {
  return useQuery({ queryKey: ['payment-routing', params], queryFn: () => fetchList<any>('/payments/processors', params) });
}

// Finance: Payment Terms
export function usePaymentTermsList(params?: Record<string, any>) {
  return useQuery({ queryKey: ['payment-terms', params], queryFn: () => fetchList<any>('/payment-terms', params) });
}

// Finance: Chargebacks
export function useChargebacks(params?: Record<string, any>) {
  return useQuery({ queryKey: ['chargebacks', params], queryFn: () => fetchList<any>('/chargebacks', params) });
}

// Finance: Credit Scoring
export function useCreditScores(params?: Record<string, any>) {
  return useQuery({ queryKey: ['credit-scores', params], queryFn: () => fetchList<any>('/credit/search', params) });
}

// Finance: Expense Reimbursement
export function useExpenseReports(params?: Record<string, any>) {
  return useQuery({ queryKey: ['expense-reports', params], queryFn: () => fetchList<any>('/payroll/expenses/reports', params) });
}

// Finance: Tax Forms
export function useForm8300Records(params?: Record<string, any>) {
  return useQuery({ queryKey: ['form-8300', params], queryFn: () => fetchList<any>('/form-8300/records', params) });
}
export function useForm4562Records(params?: Record<string, any>) {
  return useQuery({ queryKey: ['form-4562', params], queryFn: () => fetchList<any>('/form-4562/records', params) });
}
export function useScheduleM1(params?: Record<string, any>) {
  return useQuery({ queryKey: ['schedule-m1', params], queryFn: () => fetchList<any>('/schedule-m1/records', params) });
}

// ---------------------------------------------------------------------------
// Payroll
// ---------------------------------------------------------------------------
export function usePayrollACHBatches(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['payroll-ach-batches', params],
    queryFn: async () => {
      const { data } = await api.get('/payroll-ach/batches', { params });
      const items = data?.batches ?? (Array.isArray(data) ? data : []);
      return { items, total: data?.count ?? items.length, page: 1, page_size: items.length, pages: 1 };
    },
  });
}
export function usePayrollRegister(params?: Record<string, any>) {
  return useQuery({ queryKey: ['payroll-register', params], queryFn: () => fetchList<any>('/payroll/register', params) });
}
export function useGarnishments(params?: Record<string, any>) {
  return useQuery({ queryKey: ['garnishments', params], queryFn: () => fetchList<any>('/payroll/garnishments', params) });
}
export function useRetroPayCalcs(params?: Record<string, any>) {
  return useQuery({ queryKey: ['retro-pay', params], queryFn: () => fetchList<any>('/payroll/retro-pay/calculations', params) });
}
export function useTipRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['tip-records', params], queryFn: () => fetchList<any>('/payroll/tips', params) });
}
export function useInternationalPayees(params?: Record<string, any>) {
  return useQuery({ queryKey: ['international-payees', params], queryFn: () => fetchList<any>('/payroll/international/payees', params) });
}
export function useStateWageReports(params?: Record<string, any>) {
  return useQuery({ queryKey: ['state-wage-reports', params], queryFn: () => fetchList<any>('/payroll/state-wages/reports', params) });
}
export function useSUTAFilings(params?: Record<string, any>) {
  return useQuery({ queryKey: ['suta-filings', params], queryFn: () => fetchList<any>('/payroll/suta/filings', params) });
}
export function useReciprocityAgreements(params?: Record<string, any>) {
  return useQuery({ queryKey: ['reciprocity-agreements', params], queryFn: () => fetchList<any>('/payroll/reciprocity/agreements', params) });
}
export function useCertifiedPayrollReports(params?: Record<string, any>) {
  return useQuery({ queryKey: ['certified-payroll', params], queryFn: () => fetchList<any>('/payroll/certified/reports', params) });
}
export function useCSEOrders(params?: Record<string, any>) {
  return useQuery({ queryKey: ['cse-orders', params], queryFn: () => fetchList<any>('/payroll/cse/orders', params) });
}
export function useNewHireReports(params?: Record<string, any>) {
  return useQuery({ queryKey: ['new-hire-reports', params], queryFn: () => fetchList<any>('/payroll/new-hire/reports', params) });
}
export function useEWARequests(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ewa-requests', params], queryFn: () => fetchList<any>('/payroll/ewa/requests', params) });
}
export function useForm940Records(year: number = 2025, params?: Record<string, any>) {
  return useQuery({ queryKey: ['form-940', year, params], queryFn: () => fetchList<any>(`/payroll/form-940/${year}`, params) });
}
export function useForm941Records(params?: Record<string, any>) {
  return useQuery({ queryKey: ['form-941', params], queryFn: () => fetchList<any>('/payroll/form-941/records', params) });
}
export function useForm943Records(params?: Record<string, any>) {
  return useQuery({ queryKey: ['form-943', params], queryFn: () => fetchList<any>('/payroll/form-943/records', params) });
}
export function useW2Records(year: number = 2025, params?: Record<string, any>) {
  return useQuery({ queryKey: ['w2-records', year, params], queryFn: () => fetchList<any>(`/payroll/w2/discrepancies/${year}`, params) });
}
export function useW4Certificates(params?: Record<string, any>) {
  return useQuery({ queryKey: ['w4-certificates', params], queryFn: () => fetchList<any>('/payroll/w4/certificates', params) });
}
export function use1099Records(params?: Record<string, any>) {
  return useQuery({ queryKey: ['1099-records', params], queryFn: () => fetchList<any>('/1099/records', params) });
}
export function usePayrollImports(params?: Record<string, any>) {
  return useQuery({ queryKey: ['payroll-imports', params], queryFn: () => fetchList<any>('/payroll/import', params) });
}

// Payroll Dashboard & Reports
export function usePayrollDashboardCharts(params?: Record<string, any>) {
  return useQuery({ queryKey: ['payroll-dashboard-charts', params], queryFn: () => api.get('/payroll/dashboard/charts', { params }).then(r => r.data) });
}
export function usePayrollDashboardAlerts() {
  return useQuery({ queryKey: ['payroll-dashboard-alerts'], queryFn: () => api.get('/payroll/dashboard/alerts').then(r => r.data) });
}
export function usePayrollReportSummary(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['payroll-report-summary', params],
    queryFn: () => api.get('/payroll/reports/summary', { params }).then(r => r.data),
    enabled: !!(params?.start_date && params?.end_date),
  });
}
export function usePayrollReportTaxLiability(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['payroll-report-tax-liability', params],
    queryFn: () => api.get('/payroll/reports/tax-liability', { params }).then(r => r.data),
    enabled: !!params?.year,
  });
}
export function usePayrollReportLaborCost(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['payroll-report-labor-cost', params],
    queryFn: () => api.get('/payroll/reports/labor-cost', { params }).then(r => r.data),
    enabled: !!params?.year,
  });
}
export function usePayrollRunPreview(runId?: number) {
  return useQuery({
    queryKey: ['payroll-run-preview', runId],
    queryFn: () => api.get(`/payroll/runs/${runId}/preview`).then(r => r.data),
    enabled: !!runId,
  });
}
export function usePayrollAuditLog(params?: Record<string, any>) {
  return useQuery({ queryKey: ['payroll-audit-log', params], queryFn: () => api.get('/payroll/audit-log', { params }).then(r => r.data) });
}
export function useDirectDeposits(params?: Record<string, any>) {
  return useQuery({ queryKey: ['direct-deposits', params], queryFn: () => fetchList<any>('/payroll-ach/direct-deposit', params) });
}
export function useACHReturns(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ach-returns', params], queryFn: () => fetchList<any>('/payroll-ach/returns', params) });
}
export function useBatchAddEmployees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, entries }: { runId: number; entries: any[] }) =>
      api.post(`/payroll/runs/${runId}/entries/batch`, { entries }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}
export function useDismissAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) => api.patch(`/payroll/alerts/${alertId}/dismiss`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-dashboard-alerts'] }),
  });
}
export function useSnoozeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, days }: { alertId: number; days: number }) =>
      api.patch(`/payroll/alerts/${alertId}/snooze`, { days }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-dashboard-alerts'] }),
  });
}

// ---------------------------------------------------------------------------
// Payroll AI
// ---------------------------------------------------------------------------

export function usePayrollAIAsk() {
  return useMutation({
    mutationFn: (question: string) =>
      api.post('/payroll/ai/ask', { question }).then(r => r.data),
  });
}

export function usePayrollAIReviewRun() {
  return useMutation({
    mutationFn: (runId: number) =>
      api.post(`/payroll/ai/review-run/${runId}`).then(r => r.data),
  });
}

export function usePayrollAIExplainPaycheck(entryId?: number) {
  return useQuery({
    queryKey: ['payroll-ai-explain', entryId],
    queryFn: () => api.get(`/payroll/ai/explain-paycheck/${entryId}`).then(r => r.data),
    enabled: !!entryId,
  });
}

export function usePayrollAIPredictCompliance() {
  return useMutation({
    mutationFn: (body: { quarter: number; year: number }) =>
      api.post('/payroll/ai/predict-compliance', body).then(r => r.data),
  });
}

export function usePayrollAITaxOptimization() {
  return useMutation({
    mutationFn: (employeeId: number) =>
      api.post(`/payroll/ai/tax-optimization/${employeeId}`).then(r => r.data),
  });
}

export function usePayrollAIAnomalyScan() {
  return useMutation({
    mutationFn: (lookbackMonths: number = 6) =>
      api.post('/payroll/ai/anomaly-scan', { lookback_months: lookbackMonths }).then(r => r.data),
  });
}

export function usePayrollAIFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ anomalyId, isFalsePositive, notes }: { anomalyId: number; isFalsePositive: boolean; notes: string }) =>
      api.post(`/payroll/ai/feedback/${anomalyId}`, { is_false_positive: isFalsePositive, notes }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-ai-detection-stats'] }),
  });
}

export function usePayrollAIDetectionStats() {
  return useQuery({
    queryKey: ['payroll-ai-detection-stats'],
    queryFn: () => api.get('/payroll/ai/detection-stats').then(r => r.data),
  });
}

export function usePayrollAICreateCorrections() {
  return useMutation({
    mutationFn: (corrections: { entry_id: number; field: string; old_value: string; new_value: string; reason: string }[]) =>
      api.post('/payroll/ai/batch-corrections', { corrections }).then(r => r.data),
  });
}

export function usePayrollAIPreviewCorrections() {
  return useMutation({
    mutationFn: (body: { batch_id: number; corrections: { entry_id: number; field: string; old_value: string; new_value: string; reason: string }[] }) =>
      api.post('/payroll/ai/batch-corrections/preview', body).then(r => r.data),
  });
}

export function usePayrollAIApplyCorrections() {
  return useMutation({
    mutationFn: (corrections: { entry_id: number; field: string; old_value: string; new_value: string; reason: string }[]) =>
      api.post('/payroll/ai/batch-corrections/apply', { corrections }).then(r => r.data),
  });
}

export function usePayrollAICorrectionHistory(entryId?: number) {
  return useQuery({
    queryKey: ['payroll-ai-correction-history', entryId],
    queryFn: () => api.get(`/payroll/ai/correction-history/${entryId}`).then(r => r.data),
    enabled: !!entryId,
  });
}

// ---------------------------------------------------------------------------
// HR
// ---------------------------------------------------------------------------
export function useACARecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['aca-records', params], queryFn: () => fetchList<any>('/aca/records', params) });
}
export function use401kParticipants(params?: Record<string, any>) {
  return useQuery({ queryKey: ['401k-participants', params], queryFn: () => fetchList<any>('/401k/participants', params) });
}
export function use401kContributions(params?: Record<string, any>) {
  return useQuery({ queryKey: ['401k-contributions', params], queryFn: () => fetchList<any>('/401k/contributions', params) });
}
export function useERISAPlans(params?: Record<string, any>) {
  return useQuery({ queryKey: ['erisa-plans', params], queryFn: () => fetchList<any>('/erisa/plans', params) });
}
export function useLeaveRequests(params?: Record<string, any>) {
  return useQuery({ queryKey: ['leave-requests', params], queryFn: () => fetchList<any>('/hr/leave-policies', params) });
}
export function useOnboardingWorkflows(params?: Record<string, any>) {
  return useQuery({ queryKey: ['onboarding-workflows', params], queryFn: () => fetchList<any>('/onboarding/workflows', params) });
}
export function useLMSCourses(params?: Record<string, any>) {
  return useQuery({ queryKey: ['lms-courses', params], queryFn: () => fetchList<any>('/lms/courses', params) });
}

// ---------------------------------------------------------------------------
// TMS & Fleet
// ---------------------------------------------------------------------------
export function useTMSShipments(params?: Record<string, any>) {
  return useQuery({ queryKey: ['tms-shipments', params], queryFn: () => fetchList<any>('/tms-advanced/shipments', params) });
}
export function useTMSAnalytics(params?: Record<string, any>) {
  return useQuery({ queryKey: ['tms-analytics', params], queryFn: () => fetchList<any>('/tms-intelligence/analytics', params) });
}
export function useFleetTMSOrders(params?: Record<string, any>) {
  return useQuery({ queryKey: ['fleet-tms-orders', params], queryFn: () => fetchList<any>('/fleet/tms/orders', params) });
}
export function useDriverScores(params?: Record<string, any>) {
  return useQuery({ queryKey: ['driver-scores', params], queryFn: () => fetchList<any>('/fleet/driver-performance/rankings', params) });
}
export function useShippingRates(params?: Record<string, any>) {
  return useQuery({ queryKey: ['shipping-rates', params], queryFn: () => fetchList<any>('/shipping/rates', params) });
}
export function useFreightAudits(params?: Record<string, any>) {
  return useQuery({ queryKey: ['freight-audits', params], queryFn: () => fetchList<any>('/freight-audit/audits', params) });
}
export function useLoadPlans(params?: Record<string, any>) {
  return useQuery({ queryKey: ['load-plans', params], queryFn: () => fetchList<any>('/load-planning/plans', params) });
}
export function useRouteSettlements(params?: Record<string, any>) {
  return useQuery({ queryKey: ['route-settlements', params], queryFn: () => fetchList<any>('/route-accounting/settlements', params) });
}
export function useDrugAlcoholTests(params?: Record<string, any>) {
  return useQuery({ queryKey: ['drug-alcohol-tests', params], queryFn: () => fetchList<any>('/dot-fleet/tests', params) });
}
export function useRandomTestPool(params?: Record<string, any>) {
  return useQuery({ queryKey: ['random-test-pool', params], queryFn: () => fetchList<any>('/dot-fleet/random-pool', params) });
}
export function useDashcamDevices(params?: Record<string, any>) {
  return useQuery({ queryKey: ['dashcam-devices', params], queryFn: () => fetchList<any>('/dashcam/devices', params) });
}
export function useDashcamEvents(params?: Record<string, any>) {
  return useQuery({ queryKey: ['dashcam-events', params], queryFn: () => fetchList<any>('/dashcam/events', params) });
}

// ---------------------------------------------------------------------------
// Warehouse & Supply Chain
// ---------------------------------------------------------------------------
export function useWMSZones(params?: Record<string, any>) {
  return useQuery({ queryKey: ['wms-zones', params], queryFn: () => fetchList<any>('/wms/zones', params) });
}
export function useWMSBins(params?: Record<string, any>) {
  return useQuery({ queryKey: ['wms-bins', params], queryFn: () => fetchList<any>('/wms/bins', params) });
}
export function useWarehouseAdvanced(params?: Record<string, any>) {
  return useQuery({ queryKey: ['warehouse-advanced', params], queryFn: () => fetchList<any>('/warehouse-advanced/waves', params) });
}
export function useDockSchedules(params?: Record<string, any>) {
  return useQuery({ queryKey: ['dock-schedules', params], queryFn: () => fetchList<any>('/dock-scheduling/dock_appointments', params) });
}
export function useYardTrailers(params?: Record<string, any>) {
  return useQuery({ queryKey: ['yard-trailers', params], queryFn: () => fetchList<any>('/yard-management/yard_trailers', params) });
}
export function useSlottingAssignments(params?: Record<string, any>) {
  return useQuery({ queryKey: ['slotting-assignments', params], queryFn: () => fetchList<any>('/slotting/slot_assignments', params) });
}
export function useAllocations(params?: Record<string, any>) {
  return useQuery({ queryKey: ['allocations', params], queryFn: () => fetchList<any>('/allocation/efficiency', params) });
}
export function useMRPPlans(params?: Record<string, any>) {
  return useQuery({ queryKey: ['mrp-plans', params], queryFn: () => fetchList<any>('/mrp/boms', params) });
}
export function usePackHouseOrders(params?: Record<string, any>) {
  return useQuery({ queryKey: ['pack-house-orders', params], queryFn: () => fetchList<any>('/pack-house/lines', params) });
}
export function usePalletBuilds(params?: Record<string, any>) {
  return useQuery({ queryKey: ['pallet-builds', params], queryFn: () => fetchList<any>('/pallet-build/utilization', params) });
}
export function useShelfLifeAlerts(params?: Record<string, any>) {
  return useQuery({ queryKey: ['shelf-life-alerts', params], queryFn: () => fetchList<any>('/inventory/shelf-life-alerts', params) });
}
export function useGradingInspections(params?: Record<string, any>) {
  return useQuery({ queryKey: ['grading-inspections', params], queryFn: () => fetchList<any>('/grading/inspections', params) });
}
export function useCOOLRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['cool-records', params], queryFn: () => fetchList<any>('/cool/origin-report', params) });
}
export function useCAStorageRooms(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ca-storage-rooms', params], queryFn: () => fetchList<any>('/ethylene-ca/rooms', params) });
}
export function useFreshnessTracking(params?: Record<string, any>) {
  return useQuery({ queryKey: ['freshness-tracking', params], queryFn: () => fetchList<any>('/freshness/tracking', params) });
}
export function useTraceRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['trace-records', params], queryFn: () => fetchList<any>('/inventory/lots', params) });
}
export function useMockRecalls(params?: Record<string, any>) {
  return useQuery({ queryKey: ['mock-recalls', params], queryFn: () => fetchList<any>('/mock-recall/exercises', params) });
}

// ---------------------------------------------------------------------------
// Commerce
// ---------------------------------------------------------------------------
export function useECommerceChannels(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ecommerce-channels', params], queryFn: () => fetchList<any>('/ecommerce/channels', params) });
}
export function useBrokerCommissions(params?: Record<string, any>) {
  return useQuery({ queryKey: ['broker-commissions', params], queryFn: () => fetchList<any>('/broker-commission/brokers', params) });
}
export function useLoyaltyPrograms(params?: Record<string, any>) {
  return useQuery({ queryKey: ['loyalty-programs', params], queryFn: () => fetchList<any>('/loyalty/programs', params) });
}
export function useRFQs(params?: Record<string, any>) {
  return useQuery({ queryKey: ['rfqs', params], queryFn: () => fetchList<any>('/rfq/rfqs', params) });
}
export function useRecurringOrders(params?: Record<string, any>) {
  return useQuery({ queryKey: ['recurring-orders', params], queryFn: () => fetchList<any>('/recurring-orders', params) });
}
export function usePOSSessions(params?: Record<string, any>) {
  return useQuery({ queryKey: ['pos-sessions', params], queryFn: () => fetchList<any>('/pos/connections', params) });
}
export function useSubstitutionRules(params?: Record<string, any>) {
  return useQuery({ queryKey: ['substitution-rules', params], queryFn: () => fetchList<any>('/substitutions', params) });
}
export function usePriceLocks(params?: Record<string, any>) {
  return useQuery({ queryKey: ['price-locks', params], queryFn: () => fetchList<any>('/price-locks', params) });
}
export function usePricePredictions(params?: Record<string, any>) {
  return useQuery({ queryKey: ['price-predictions', params], queryFn: () => fetchList<any>('/predictive-pricing/predict', params) });
}
export function useMenuPlans(params?: Record<string, any>) {
  return useQuery({ queryKey: ['menu-plans', params], queryFn: () => fetchList<any>('/menu-planning/menus', params) });
}
export function useProducePreferences(params?: Record<string, any>) {
  return useQuery({ queryKey: ['produce-preferences', params], queryFn: () => fetchList<any>('/customer-preferences/produce', params) });
}

// ---------------------------------------------------------------------------
// Compliance (Extended)
// ---------------------------------------------------------------------------
export function usePermits(params?: Record<string, any>) {
  return useQuery({ queryKey: ['permits', params], queryFn: () => fetchList<any>('/permits', params) });
}
export function usePACATrustRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['paca-trust', params], queryFn: () => fetchList<any>('/paca-trust/accounts', params) });
}
export function useBioterrorismRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['bioterrorism', params], queryFn: () => fetchList<any>('/bioterrorism/prior-notices', params) });
}
export function useBCPPlans(params?: Record<string, any>) {
  return useQuery({ queryKey: ['bcp-plans', params], queryFn: () => fetchList<any>('/bcp/plans', params) });
}
export function useCFATSFacilities(params?: Record<string, any>) {
  return useQuery({ queryKey: ['cfats', params], queryFn: () => fetchList<any>('/cfats/facilities', params) });
}
export function useCarbonESGRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['carbon-esg', params], queryFn: () => fetchList<any>('/carbon/food-miles', params) });
}
export function useCommodityAssessments(params?: Record<string, any>) {
  return useQuery({ queryKey: ['commodity-assessments', params], queryFn: () => fetchList<any>('/commodity-assessments', params) });
}
export function useDataBreachRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['data-breach', params], queryFn: () => fetchList<any>('/data-breach/notifications', params) });
}
export function useFCRARecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['fcra-records', params], queryFn: () => fetchList<any>('/fcra/authorizations', params) });
}
export function useFDAFurls(params?: Record<string, any>) {
  return useQuery({ queryKey: ['fda-furls', params], queryFn: () => fetchList<any>('/fda/registrations', params) });
}
export function useFDARegistration(params?: Record<string, any>) {
  return useQuery({ queryKey: ['fda-registration', params], queryFn: () => fetchList<any>('/fda-registration/registrations', params) });
}
export function useFSMATransportRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['fsma-transport', params], queryFn: () => fetchList<any>('/fsma-transport/sanitation', params) });
}
export function useImportDutyRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['import-duty', params], queryFn: () => fetchList<any>('/import-duty/trade-agreements', params) });
}
export function useMarketingComplianceRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['marketing-compliance', params], queryFn: () => fetchList<any>('/marketing-compliance/records', params) });
}
export function useOFACSanctionsRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ofac-sanctions', params], queryFn: () => fetchList<any>('/ofac/screenings', params) });
}

// ---------------------------------------------------------------------------
// Regulatory
// ---------------------------------------------------------------------------
export function useOrganicEnforcement(params?: Record<string, any>) {
  return useQuery({ queryKey: ['organic-enforcement', params], queryFn: () => fetchList<any>('/organic-enforcement/certifications', params) });
}
export function usePCICompliance(params?: Record<string, any>) {
  return useQuery({ queryKey: ['pci-compliance', params], queryFn: () => fetchList<any>('/pci-compliance/saq', params) });
}
export function usePhytosanitaryRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['phytosanitary', params], queryFn: () => fetchList<any>('/phytosanitary/phyto_certificates', params) });
}
export function usePrivacyRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['privacy-records', params], queryFn: () => fetchList<any>('/privacy/ccpa/requests', params) });
}
export function useStateOrganicRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['state-organic', params], queryFn: () => fetchList<any>('/state-organic/certifications', params) });
}
export function useUSERRARecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['userra-records', params], queryFn: () => fetchList<any>('/userra/leaves', params) });
}
export function useWARNActRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['warn-act', params], queryFn: () => fetchList<any>('/warn-act/events', params) });
}
export function useWeightsMeasuresRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['weights-measures', params], queryFn: () => fetchList<any>('/weights-measures/scales', params) });
}

// ---------------------------------------------------------------------------
// VA Tax
// ---------------------------------------------------------------------------
export function useVirginiaCompliance(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va-compliance', params], queryFn: () => fetchOne<any>('/virginia-compliance/log') });
}
export function useVAST1(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va-st1', params], queryFn: () => fetchList<any>('/va-tax/st1/deadlines', params) });
}
export function useVA4Certificate(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va4-certificates', params], queryFn: () => fetchList<any>('/va-tax/va4/pending', params) });
}
export function useVA5Filing(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va5-filings', params], queryFn: () => fetchList<any>('/va-tax/va5/summary', params) });
}
export function useVA15Deposit(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va15-deposits', params], queryFn: () => fetchList<any>('/va-tax/va15/pending', params) });
}
export function useVA16Reconciliation(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va16-reconciliations', params], queryFn: () => fetchList<any>('/va-tax/va16/summary', params) });
}
export function useVAWithholding(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va-withholding', params], queryFn: () => fetchList<any>('/va-tax/withholding/table/history', params) });
}
export function useVACorporateTax(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va-corporate-tax', params], queryFn: () => fetchList<any>('/va-tax/corporate/estimates', params) });
}
export function useVAIFTA(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va-ifta', params], queryFn: () => fetchList<any>('/va-tax/ifta/vehicles', params) });
}
export function useVAFilingFrequency(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va-filing-frequency', params], queryFn: () => fetchList<any>('/va-tax/filing-frequency/history', params) });
}
export function useVAPenaltyInterest(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va-penalty-interest', params], queryFn: () => fetchList<any>('/va-tax/penalties', params) });
}
export function useVATangibleProperty(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va-tangible-property', params], queryFn: () => fetchList<any>('/va-tax/tangible-property/returns', params) });
}
export function useVATaxIntelligence(params?: Record<string, any>) {
  return useQuery({ queryKey: ['va-tax-intelligence', params], queryFn: () => fetchList<any>('/va-tax/intelligence', params) });
}
export function useVDACS(params?: Record<string, any>) {
  return useQuery({ queryKey: ['vdacs', params], queryFn: () => fetchList<any>('/vdacs/warehouse-permits', params) });
}
export function useVDHHealth(params?: Record<string, any>) {
  return useQuery({ queryKey: ['vdh-health', params], queryFn: () => fetchList<any>('/vdh-health/permits', params) });
}

// ---------------------------------------------------------------------------
// AI
// ---------------------------------------------------------------------------
export function useAICopilotQuery() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (payload: Record<string, unknown>) => api.post('/ai-copilot/query', payload), onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-copilot'] }) });
}
export function useAIPredictions(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-predictions', params], queryFn: () => fetchList<any>('/ai-copilot/predictions', params) });
}
export function useAIEmails(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-emails', params], queryFn: () => fetchList<any>('/ai/emails', params) });
}
export function useAIQualityAnalysis(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-quality', params], queryFn: () => fetchList<any>('/ai/quality/analysis', params) });
}
export function useAIOrderChat(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-order-chat', params], queryFn: () => fetchList<any>('/ai/order-chat/sessions', params) });
}
export function useMLForecast(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ml-forecast', params], queryFn: () => fetchList<any>('/ml/forecast', params) });
}
export function useDemandForecast(params?: Record<string, any>) {
  return useQuery({ queryKey: ['demand-forecast', params], queryFn: () => fetchList<any>('/demand/forecasts', params) });
}
export function useMarketData(params?: Record<string, any>) {
  return useQuery({ queryKey: ['market-data', params], queryFn: () => fetchList<any>('/market-intelligence/data', params) });
}
export function useVoiceAgentSessions(params?: Record<string, any>) {
  return useQuery({ queryKey: ['voice-agent', params], queryFn: () => fetchList<any>('/voice-agent/calls', params) });
}
export function useFinancialInsights(params?: Record<string, any>) {
  return useQuery({ queryKey: ['financial-insights', params], queryFn: () => fetchList<any>('/financial-intelligence/insights', params) });
}
export function useVendorCostAnalysis(params?: Record<string, any>) {
  return useQuery({ queryKey: ['vendor-cost-analysis', params], queryFn: () => fetchList<any>('/vendor-costs/weekly', params) });
}
export function useProfitInsights(params?: Record<string, any>) {
  return useQuery({ queryKey: ['profit-insights', params], queryFn: () => fetchList<any>('/profit/insights', params) });
}

// ---------------------------------------------------------------------------
// AI Domain Modules (HR, Sales, Warehouse, Contract)
// ---------------------------------------------------------------------------
export function useAITalentMatching(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-talent-matching', params], queryFn: () => fetchList<any>('/ai-talent-matching/match', params) });
}
export function useAIRetentionRisk(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-retention-risk', params], queryFn: () => fetchList<any>('/ai-retention-risk/batch', params) });
}
export function useAIWorkforcePlanning(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-workforce-planning', params], queryFn: () => fetchList<any>('/ai-workforce-planning/forecast', params) });
}
export function useAILeadScoring(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-lead-scoring', params], queryFn: () => fetchList<any>('/ai-lead-scoring/batch', params) });
}
export function useAIChurnPrevention(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-churn-prevention', params], queryFn: () => fetchList<any>('/ai-churn-prevention/at-risk', params) });
}
export function useAISalesForecasting(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-sales-forecasting', params], queryFn: () => fetchList<any>('/ai-sales-forecasting/predict', params) });
}
export function useAIPickOptimization(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-pick-optimization', params], queryFn: () => fetchList<any>('/ai-pick-optimization/picker-balance', params) });
}
export function useAIInventoryReplenishment(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-inventory-replenishment', params], queryFn: () => fetchList<any>('/ai-inventory-replenishment/batch-recommendations', params) });
}
export function useAISlottingAdvisor(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-slotting-advisor', params], queryFn: () => fetchList<any>('/ai-slotting-advisor/velocity-report', params) });
}
export function useAIContractAnalyzer(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-contract-analyzer', params], queryFn: () => fetchList<any>('/ai-contract-analyzer/expiring', params) });
}
export function useAIContractNegotiation(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ai-contract-negotiation', params], queryFn: () => fetchList<any>('/ai-contract-negotiation/savings-opportunities', params) });
}

// ---------------------------------------------------------------------------
// Admin (Extended)
// ---------------------------------------------------------------------------
export function useSSOProviders(params?: Record<string, any>) {
  return useQuery({ queryKey: ['sso-providers', params], queryFn: () => fetchList<any>('/sso/providers', params) });
}
export function useSSOSessions(params?: Record<string, any>) {
  return useQuery({ queryKey: ['sso-sessions', params], queryFn: () => fetchList<any>('/sso/sessions', params) });
}
export function useOAuthClients(params?: Record<string, any>) {
  return useQuery({ queryKey: ['oauth-clients', params], queryFn: () => fetchList<any>('/oauth/clients', params) });
}
export function useAPIGatewayKeys(params?: Record<string, any>) {
  return useQuery({ queryKey: ['api-gateway-keys', params], queryFn: () => fetchList<any>('/api-gateway/keys', params) });
}
export function useNotificationChannels(params?: Record<string, any>) {
  return useQuery({ queryKey: ['notification-channels', params], queryFn: () => fetchList<any>('/notifications/channels', params) });
}
export function useEncryptionKeys(params?: Record<string, any>) {
  return useQuery({ queryKey: ['encryption-keys', params], queryFn: () => fetchList<any>('/key-rotation/keys', params) });
}
export function useArchivalPolicies(params?: Record<string, any>) {
  return useQuery({ queryKey: ['archival-policies', params], queryFn: () => fetchList<any>('/archival/policies', params) });
}
export function useMigrationJobs(params?: Record<string, any>) {
  return useQuery({ queryKey: ['migration-jobs', params], queryFn: () => fetchList<any>('/migration/history', params) });
}
export function useSearchConfig(params?: Record<string, any>) {
  return useQuery({ queryKey: ['search-config', params], queryFn: () => fetchOne<any>('/search/config') });
}
export function useHealthStatus(params?: Record<string, any>) {
  return useQuery({ queryKey: ['health-status', params], queryFn: () => fetchOne<any>('/health') });
}
export function useDocuSignEnvelopes(params?: Record<string, any>) {
  return useQuery({ queryKey: ['docusign-envelopes', params], queryFn: () => fetchList<any>('/esign/envelopes', params) });
}
export function useSlackWebhooks(params?: Record<string, any>) {
  return useQuery({ queryKey: ['slack-webhooks', params], queryFn: () => fetchList<any>('/webhooks/slack', params) });
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------
export function useBIDashboards(params?: Record<string, any>) {
  return useQuery({ queryKey: ['bi-dashboards', params], queryFn: () => fetchList<any>('/bi/dashboards', params) });
}
export function useReportDefinitions(params?: Record<string, any>) {
  return useQuery({ queryKey: ['report-definitions', params], queryFn: () => fetchList<any>('/report-builder/definitions', params) });
}
export function useAsyncReports(params?: Record<string, any>) {
  return useQuery({ queryKey: ['async-reports', params], queryFn: () => fetchList<any>('/reports/async', params) });
}

// ---------------------------------------------------------------------------
// Sales Analytics
// ---------------------------------------------------------------------------

export interface SalesAnalyticsSummary {
  period_days: number;
  revenue: number;
  revenue_trend: number;
  order_count: number;
  order_trend: number;
  avg_order_value: number;
  top_customers: { id: number; name: string; code: string; order_count: number; total_revenue: number }[];
  daily_trend: { date: string; orders: number; revenue: number }[];
  status_breakdown: { status: string; count: number }[];
}

export interface PipelineSummary {
  stages: { stage: string; count: number; total_value: number; avg_probability: number }[];
  total_opportunities: number;
  won: number;
  lost: number;
  win_rate: number;
  weighted_pipeline: number;
}

export interface RepPerformance {
  period_days: number;
  reps: { rep: string; order_count: number; revenue: number; avg_deal_size: number }[];
}

export function useSalesAnalytics(periodDays = 30) {
  return useQuery({
    queryKey: ['sales-analytics', periodDays],
    queryFn: () => fetchOne<SalesAnalyticsSummary>(`/sales/analytics/summary?period_days=${periodDays}`),
  });
}

export function usePipelineSummary() {
  return useQuery({
    queryKey: ['pipeline-summary'],
    queryFn: () => fetchOne<PipelineSummary>('/sales/analytics/pipeline'),
  });
}

export function useRepPerformance(periodDays = 30) {
  return useQuery({
    queryKey: ['rep-performance', periodDays],
    queryFn: () => fetchOne<RepPerformance>(`/sales/analytics/rep-performance?period_days=${periodDays}`),
  });
}

// ---------------------------------------------------------------------------
// Field Visits
// ---------------------------------------------------------------------------

export interface FieldVisit {
  id: number;
  salesperson_id: string;
  customer_id: number;
  customer_name: string;
  visit_date: string;
  visit_type: string;
  check_in_time: string | null;
  check_out_time: string | null;
  notes: string | null;
  orders_placed: number;
  total_sales: number;
}

export function useFieldVisits(params?: PaginationParams & {
  salesperson_id?: string;
  customer_id?: number;
  visit_type?: string;
  date_from?: string;
  date_to?: string;
}) {
  return useQuery({
    queryKey: ['field-visits', params],
    queryFn: () => fetchPaginated<FieldVisit>('/sales/field-visits', params),
  });
}

export function useCreateFieldVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      customer_id: number;
      visit_date: string;
      visit_type: string;
      check_in_time?: string;
      notes?: string;
      orders_placed?: number;
      total_sales?: number;
    }) => api.post('/sales/field-visits', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['field-visits'] }),
  });
}

export function useUpdateFieldVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; check_out_time?: string; notes?: string; orders_placed?: number; total_sales?: number }) =>
      api.patch(`/sales/field-visits/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['field-visits'] }),
  });
}

// ---------------------------------------------------------------------------
// Returns / RMA
// ---------------------------------------------------------------------------

export interface RMA {
  id: number;
  rma_number: string;
  customer_id: number;
  customer_name: string;
  so_id: number;
  reason: string;
  status: string;
  credit_amount: number;
  disposition: string;
  assigned_to: string;
  created_at: string | null;
}

export function useRMAs(params?: PaginationParams & { status?: string; customer_id?: number }) {
  return useQuery({
    queryKey: ['rmas', params],
    queryFn: () => fetchPaginated<RMA>('/rma/list', params),
  });
}

export function useCreateRMA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      customer_id: number;
      so_id: number;
      items: { product_id: number; quantity: number; reason: string }[];
      reason: string;
    }) => api.post('/order-automation/rma', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rmas'] }),
  });
}

export function useApproveRMA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rmaId: number) => api.post(`/order-automation/rma/${rmaId}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rmas'] }),
  });
}

export function useReceiveRMA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rmaId, received_items, inspection_notes }: {
      rmaId: number;
      received_items: { product_id: number; quantity: number; condition: string }[];
      inspection_notes: string;
    }) => api.post(`/order-automation/rma/${rmaId}/receive`, { received_items, inspection_notes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rmas'] }),
  });
}

export function useCreditRMA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rmaId: number) => api.post(`/order-automation/rma/${rmaId}/credit`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rmas'] }),
  });
}

// ---------------------------------------------------------------------------
// Warranty
// ---------------------------------------------------------------------------

export interface WarrantyPolicy {
  id: number;
  name: string;
  product_category: string;
  duration_months: number;
  coverage_type: string;
  max_claims: number;
  max_claim_value: number;
  status: string;
}

export interface WarrantyRegistration {
  id: number;
  reg_number: string;
  customer_id: number;
  customer_name: string;
  product_id: number;
  serial_number: string;
  policy_id: number;
  policy_name: string;
  registered_date: string;
  expires_date: string;
  claims_used: number;
  status: string;
}

export interface WarrantyClaim {
  id: number;
  claim_number: string;
  registration_id: number;
  reg_number: string;
  customer_name: string;
  product_id: number | null;
  claim_date: string;
  issue_description: string;
  claim_amount: number;
  status: string;
  resolution: string;
}

export interface WarrantyAnalytics {
  kpis: {
    active_policies: number;
    active_registrations: number;
    open_claims: number;
    expiring_soon: number;
  };
  claims_by_status: { status: string; count: number }[];
  claims_by_category: { category: string; count: number }[];
  financial_summary: {
    total_filed: number;
    total_claimed_value: number;
    total_paid_out: number;
    avg_claim_value: number;
  };
}

export function useWarrantyPolicies(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['warranty-policies', params],
    queryFn: () => fetchPaginated<WarrantyPolicy>('/warranty/policies', params),
  });
}

export function useWarrantyRegistrations(params?: PaginationParams & { status?: string; customer_id?: number }) {
  return useQuery({
    queryKey: ['warranty-registrations', params],
    queryFn: () => fetchPaginated<WarrantyRegistration>('/warranty/registrations', params),
  });
}

export function useWarrantyClaims(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: ['warranty-claims', params],
    queryFn: () => fetchPaginated<WarrantyClaim>('/warranty/claims', params),
  });
}

export function useWarrantyAnalytics() {
  return useQuery({
    queryKey: ['warranty-analytics'],
    queryFn: () => fetchOne<WarrantyAnalytics>('/warranty/analytics'),
  });
}

export function useCreateWarrantyPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      product_category: string;
      duration_months: number;
      coverage_type?: string;
      max_claims?: number;
      max_claim_value?: number;
    }) => api.post('/warranty/policies', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty-policies'] }),
  });
}

export function useCreateWarrantyRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { customer_id: number; product_id: number; serial_number: string; policy_id: number }) =>
      api.post('/warranty/registrations', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-registrations'] });
      qc.invalidateQueries({ queryKey: ['warranty-analytics'] });
    },
  });
}

export function useFileWarrantyClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { registration_id: number; issue_description: string; claim_amount?: number }) =>
      api.post('/warranty/claims', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-claims'] });
      qc.invalidateQueries({ queryKey: ['warranty-analytics'] });
    },
  });
}

export function useApproveWarrantyClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ claimId, resolution }: { claimId: number; resolution?: string }) =>
      api.post(`/warranty/claims/${claimId}/approve`, { resolution }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-claims'] });
      qc.invalidateQueries({ queryKey: ['warranty-analytics'] });
    },
  });
}

export function useDenyWarrantyClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ claimId, reason }: { claimId: number; reason?: string }) =>
      api.post(`/warranty/claims/${claimId}/deny`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty-claims'] });
      qc.invalidateQueries({ queryKey: ['warranty-analytics'] });
    },
  });
}

// ---------------------------------------------------------------------------
// CRM Pipeline (Opportunities)
// ---------------------------------------------------------------------------

export interface CRMOpportunity {
  id: number;
  customer_id: number;
  customer_name: string;
  title: string;
  stage: string;
  status: string;
  value: number;
  probability: number;
  expected_close: string | null;
  assigned_to: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export function useCRMOpportunities(params?: PaginationParams & { stage?: string; status?: string }) {
  return useQuery({
    queryKey: ['crm-opportunities', params],
    queryFn: () => fetchPaginated<CRMOpportunity>('/crm/opportunities', params),
  });
}

export function useCreateCRMOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      customer_id: number;
      title: string;
      stage?: string;
      value?: number;
      probability?: number;
      expected_close?: string;
      notes?: string;
    }) => api.post('/crm/opportunities', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm-opportunities'] });
      qc.invalidateQueries({ queryKey: ['pipeline-summary'] });
    },
  });
}

export function useUpdateCRMOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: number;
      stage?: string;
      status?: string;
      value?: number;
      probability?: number;
      expected_close?: string;
      notes?: string;
    }) => api.patch(`/crm/opportunities/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm-opportunities'] });
      qc.invalidateQueries({ queryKey: ['pipeline-summary'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Admin Hub
// ---------------------------------------------------------------------------

export interface AdminDashboard {
  system_health: {
    database: string;
    redis: string;
    uptime_seconds: number;
    python_version: string;
    pid: number;
  };
  scheduler: {
    status: string;
    total: number;
    enabled: number;
    disabled: number;
    last_5_runs: SchedulerJob[];
  };
  reconciliation: ReconSummaryItem[];
  integration_health: {
    total: number;
    healthy: number;
    failed: number;
    details: Record<string, string>;
  };
  quick_links: { label: string; path: string }[];
}

export interface SchedulerJob {
  job_type: string;
  hour: number | null;
  minute: number;
  interval_minutes: number | null;
  day_of_week: number | null;
  enabled: boolean;
  last_run: string | null;
  schedule_description: string;
}

export interface ReconSummaryItem {
  name: string;
  type: string;
  detail_url: string;
  latest_status: string;
  latest_run_date: string | null;
  variance_count: number;
}

export function useAdminDashboard() {
  return useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => api.get<AdminDashboard>('/admin-hub/dashboard').then((r) => r.data),
    refetchInterval: 60_000,
  });
}

export function useSchedulerJobs() {
  return useQuery({
    queryKey: ['scheduler-jobs'],
    queryFn: () => api.get<SchedulerJob[]>('/admin-hub/scheduler/jobs').then((r) => r.data),
  });
}

export function useToggleSchedulerJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobType, enabled }: { jobType: string; enabled: boolean }) =>
      api.post(`/admin-hub/scheduler/jobs/${jobType}/toggle`, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scheduler-jobs'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    },
  });
}

export function useTriggerSchedulerJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobType: string) =>
      api.post(`/admin-hub/scheduler/jobs/${jobType}/trigger`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scheduler-jobs'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    },
  });
}

export function useFlushCache() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scope: string) =>
      api.post('/admin-hub/utilities/cache/flush', { scope }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    },
  });
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => api.get('/admin-hub/setup/feature-flags').then((r) => r.data),
  });
}

export function useUpdateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flagName, enabled }: { flagName: string; enabled: boolean }) =>
      api.put(`/admin-hub/setup/feature-flags/${flagName}`, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feature-flags'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    },
  });
}

// =====================================================================
// Period Close Orchestrator
// =====================================================================

export function usePeriodCloseCalendar(fiscalYear?: number) {
  return useQuery({
    queryKey: ['period-close-calendar', fiscalYear],
    queryFn: () =>
      api.get('/period-close/calendar', { params: { fiscal_year: fiscalYear } }).then((r) => r.data),
  });
}

export function usePeriodCloseRunBooks() {
  return useQuery({
    queryKey: ['period-close-runbooks'],
    queryFn: () => api.get('/period-close/run-books').then((r) => r.data),
  });
}

export function usePeriodCloseRunBook(cadence: string | null) {
  return useQuery({
    queryKey: ['period-close-runbook', cadence],
    queryFn: () => api.get(`/period-close/run-books/${cadence}`).then((r) => r.data),
    enabled: !!cadence,
  });
}

export function usePeriodCloseRuns(params?: { cadence?: string; status?: string }) {
  return useQuery({
    queryKey: ['period-close-runs', params],
    queryFn: () => api.get('/period-close/runs', { params }).then((r) => r.data),
  });
}

export function usePeriodCloseRunDetail(runId: number | null) {
  return useQuery({
    queryKey: ['period-close-run', runId],
    queryFn: () => api.get(`/period-close/runs/${runId}`).then((r) => r.data),
    enabled: !!runId,
    refetchInterval: 5_000,
  });
}

export function usePeriodCloseDeadlines(fiscalYear?: number) {
  return useQuery({
    queryKey: ['period-close-deadlines', fiscalYear],
    queryFn: () =>
      api.get('/period-close/deadlines', { params: { fiscal_year: fiscalYear } }).then((r) => r.data),
  });
}

export function useStartPeriodCloseRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { cadence: string; fiscal_year: number; fiscal_month?: number; fiscal_quarter?: number }) =>
      api.post('/period-close/runs', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['period-close-runs'] });
    },
  });
}

export function useExecutePeriodCloseStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, stepKey }: { runId: number; stepKey: string }) =>
      api.post(`/period-close/runs/${runId}/steps/${stepKey}/execute`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['period-close-run'] });
      qc.invalidateQueries({ queryKey: ['period-close-runs'] });
    },
  });
}

export function useSkipPeriodCloseStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, stepKey, reason }: { runId: number; stepKey: string; reason?: string }) =>
      api.post(`/period-close/runs/${runId}/steps/${stepKey}/skip`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['period-close-run'] });
      qc.invalidateQueries({ queryKey: ['period-close-runs'] });
    },
  });
}

export function useCompletePeriodCloseStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, stepKey, result_summary }: { runId: number; stepKey: string; result_summary?: Record<string, unknown> }) =>
      api.post(`/period-close/runs/${runId}/steps/${stepKey}/complete`, { result_summary }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['period-close-run'] });
      qc.invalidateQueries({ queryKey: ['period-close-runs'] });
    },
  });
}

export function useExecuteAllPeriodCloseSteps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) =>
      api.post(`/period-close/runs/${runId}/execute-all`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['period-close-run'] });
      qc.invalidateQueries({ queryKey: ['period-close-runs'] });
    },
  });
}

// ---------------------------------------------------------------------------
// GL Batch Operations
// ---------------------------------------------------------------------------

export function useBatchApproveGL() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { entry_ids: number[]; approver_role?: string }) =>
      api.post('/accounting/gl/batch/approve', params).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gl-entries'] }),
  });
}

// ---------------------------------------------------------------------------
// Global Search
// ---------------------------------------------------------------------------

export interface GlobalSearchResult {
  id: number | string;
  type: string;
  title: string;
  subtitle?: string;
  score?: number;
}

export interface GlobalSearchResponse {
  results: GlobalSearchResult[];
  total: number;
}

export function useGlobalSearch(query: string) {
  return useQuery({
    queryKey: ['global-search', query],
    queryFn: () => api.get<GlobalSearchResponse>('/search/global', { params: { q: query, limit: 15 } }).then(r => r.data),
    enabled: query.length >= 2,
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// Social Media
// ---------------------------------------------------------------------------

export function useSocialMediaAccounts(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['social-media-accounts', params],
    queryFn: () => fetchList<any>('/marketing/social-media/accounts', params),
  });
}

export function useDisconnectSocialAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/marketing/social-media/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social-media-accounts'] }),
  });
}

export function useRefreshSocialToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/marketing/social-media/accounts/${id}/refresh`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social-media-accounts'] }),
  });
}

export function useSocialMediaOAuthUrl() {
  return useMutation({
    mutationFn: ({ platform, redirect_uri }: { platform: string; redirect_uri: string }) =>
      api.get(`/marketing/social-media/oauth/${platform}/authorize`, { params: { redirect_uri } }),
  });
}

export function useSocialMediaOAuthCallback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { platform: string; code: string; state: string; redirect_uri: string }) =>
      api.post(`/marketing/social-media/oauth/${payload.platform}/callback`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social-media-accounts'] }),
  });
}

// ---------------------------------------------------------------------------
// AI Daily Planner ("My Day")
// ---------------------------------------------------------------------------

export function useMyDay() {
  return useQuery({
    queryKey: ['my-day'],
    queryFn: () => api.get('/ai-assistant/my-day').then(r => r.data),
    staleTime: 60_000,
    retry: 1,
  });
}

export function useRefreshMyDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/ai-assistant/my-day/refresh').then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-day'] }),
  });
}

export function useCompletePlanItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.post(`/ai-assistant/my-day/items/${itemId}/complete`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-day'] }),
  });
}

export function useSnoozePlanItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, until_date }: { itemId: string; until_date: string }) =>
      api.post(`/ai-assistant/my-day/items/${itemId}/snooze`, { until_date }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-day'] }),
  });
}

export function useDismissPlanItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.post(`/ai-assistant/my-day/items/${itemId}/dismiss`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-day'] }),
  });
}

// ---------------------------------------------------------------------------
// Payroll Automation
// ---------------------------------------------------------------------------

export function usePayrollAutomationConfig() {
  return useQuery({
    queryKey: ['payroll-automation-config'],
    queryFn: () => api.get('/payroll/automation/config').then(r => r.data),
  });
}

export function usePayrollAutomationConfigUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.put('/payroll/automation/config', payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-automation-config'] });
      qc.invalidateQueries({ queryKey: ['payroll-automation-dashboard'] });
    },
  });
}

export function usePayrollAutomationDashboard() {
  return useQuery({
    queryKey: ['payroll-automation-dashboard'],
    queryFn: () => api.get('/payroll/automation/dashboard').then(r => r.data),
  });
}

export function usePayrollAutomationPipeline(runId?: number) {
  return useQuery({
    queryKey: ['payroll-automation-pipeline', runId],
    queryFn: () => api.get(`/payroll/automation/pipeline/${runId}`).then(r => r.data),
    enabled: !!runId,
  });
}

export function usePayrollAutomationTrigger() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period_start: string; period_end: string; pay_date: string; run_type?: string }) =>
      api.post('/payroll/automation/trigger', body).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-automation-dashboard'] });
    },
  });
}

// Phase 2: AI Approval hooks
export function usePayrollAIApprovalHistory() {
  return useQuery({
    queryKey: ['payroll-ai-approval-history'],
    queryFn: () => api.get('/payroll/automation/ai-approval/history').then(r => r.data),
  });
}

export function usePayrollAIApprovalEvaluate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) =>
      api.post(`/payroll/automation/ai-approval/evaluate/${runId}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-ai-approval-history'] });
      qc.invalidateQueries({ queryKey: ['payroll-automation-dashboard'] });
    },
  });
}

// Phase 2: GL Reconciliation hooks
export function usePayrollGLReconciliation(runId: number) {
  return useQuery({
    queryKey: ['payroll-gl-recon', runId],
    queryFn: () => api.get(`/payroll/automation/gl-recon/${runId}`).then(r => r.data),
    enabled: !!runId,
  });
}

export function usePayrollGLReconciliationTrigger() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) =>
      api.post(`/payroll/automation/gl-recon/${runId}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-gl-recon'] });
    },
  });
}

// Phase 2: Termination, Year-End, Outlier hooks
export function usePayrollPendingTerminations() {
  return useQuery({
    queryKey: ['payroll-pending-terminations'],
    queryFn: () => api.get('/payroll/automation/terminations/pending').then(r => r.data),
  });
}

export function usePayrollYearEndTrigger() {
  return useMutation({
    mutationFn: (taxYear: number) =>
      api.post(`/payroll/automation/year-end/${taxYear}`).then(r => r.data),
  });
}

export function usePayrollYearEndStatus(taxYear: number) {
  return useQuery({
    queryKey: ['payroll-year-end-status', taxYear],
    queryFn: () => api.get(`/payroll/automation/year-end/${taxYear}/status`).then(r => r.data),
    enabled: !!taxYear,
  });
}

export function usePayrollOutliers() {
  return useQuery({
    queryKey: ['payroll-outliers'],
    queryFn: () => api.get('/payroll/automation/outliers').then(r => r.data),
  });
}

// Phase 3: AI Forecast
export function usePayrollForecast(monthsAhead = 1, monthsLookback = 6) {
  return useQuery({
    queryKey: ['payroll-forecast', monthsAhead, monthsLookback],
    queryFn: () =>
      api
        .get('/payroll/forecast', { params: { months_ahead: monthsAhead, months_lookback: monthsLookback } })
        .then(r => r.data),
  });
}

// Phase 3: Anomaly Explain
export function usePayrollAnomalyExplain() {
  return useMutation({
    mutationFn: (anomalyId: number) =>
      api.post(`/payroll/anomalies/${anomalyId}/explain`).then(r => r.data),
  });
}

// Phase 3: Anomaly Auto-Correct
export function usePayrollAnomalyAutoCorrect() {
  return useMutation({
    mutationFn: (anomalyId: number) =>
      api.post(`/payroll/anomalies/${anomalyId}/auto-correct`).then(r => r.data),
  });
}

// Phase 3: Benefits Recon Import
export function usePayrollBenefitsReconImport() {
  return useMutation({
    mutationFn: (data: { vendor_name: string; formData: FormData }) =>
      api
        .post(`/payroll/benefits-recon/import?vendor_name=${encodeURIComponent(data.vendor_name)}`, data.formData)
        .then(r => r.data),
  });
}

// Phase 3: Benefits Recon Results
export function usePayrollBenefitsReconResults(vendorName = 'all') {
  return useQuery({
    queryKey: ['payroll-benefits-recon', vendorName],
    queryFn: () =>
      api.get('/payroll/benefits-recon/results', { params: { vendor_name: vendorName } }).then(r => r.data),
  });
}

// Phase 3: Bank Recon Import
export function usePayrollBankReconImport() {
  return useMutation({
    mutationFn: (data: { format: string; formData: FormData }) =>
      api
        .post(`/payroll/bank-recon/import?format=${encodeURIComponent(data.format)}`, data.formData)
        .then(r => r.data),
  });
}

// Phase 3: Bank Recon Results
export function usePayrollBankReconResults() {
  return useQuery({
    queryKey: ['payroll-bank-recon'],
    queryFn: () => api.get('/payroll/bank-recon/results').then(r => r.data),
  });
}

// Phase 3: EWA Auto-Approval Stats
export function usePayrollEWAAutoApprovalStats() {
  return useQuery({
    queryKey: ['payroll-ewa-auto-approval-stats'],
    queryFn: () => api.get('/payroll/ewa/auto-approval/stats').then(r => r.data),
  });
}

// ==============================================================
//  Cross-Domain Automation Dashboard
// ==============================================================

export function useAutomationDashboardStatus() {
  return useQuery({
    queryKey: ['automation-dashboard-status'],
    queryFn: () => api.get('/automation-dashboard/status').then(r => r.data),
  });
}

export function useAutomationDashboardRuns(params?: {
  limit?: number;
  offset?: number;
  domain?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: ['automation-dashboard-runs', params],
    queryFn: () =>
      api.get('/automation-dashboard/runs', { params }).then(r => r.data),
  });
}

export function useToggleAutomationRule() {
  return useMutation({
    mutationFn: (ruleId: string) =>
      api.post(`/automation-dashboard/rules/${encodeURIComponent(ruleId)}/toggle`).then(r => r.data),
  });
}

export function useAutomationDashboardMetrics() {
  return useQuery({
    queryKey: ['automation-dashboard-metrics'],
    queryFn: () => api.get('/automation-dashboard/metrics').then(r => r.data),
    refetchInterval: 60_000,
  });
}

export function useAutomationDashboardHealth() {
  return useQuery({
    queryKey: ['automation-dashboard-health'],
    queryFn: () => api.get('/automation-dashboard/health').then(r => r.data),
  });
}

// ==============================================================
//  Order Intake Pipeline
// ==============================================================

export interface IntakeDraft {
  id: number;
  customer_id: number;
  status: string;
  source: string;
  suggested_total: number;
  final_total: number | null;
  item_count: number;
  needs_review: boolean;
  avg_confidence: number | null;
  audio_url: string | null;
  sales_order_id: number | null;
  rejection_reason: string | null;
  expires_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export function useOrderIntakeQueue(params?: {
  page?: number;
  per_page?: number;
  source?: string;
  customer_id?: number;
}) {
  return useQuery({
    queryKey: ['order-intake-queue', params],
    queryFn: () =>
      api.get('/order-intake/queue', { params }).then(r => r.data),
    refetchInterval: 30_000,
  });
}

export function useOrderIntakeDraft(draftId: number | null) {
  return useQuery({
    queryKey: ['order-intake-draft', draftId],
    queryFn: () =>
      api.get(`/order-intake/queue/${draftId}`).then(r => r.data),
    enabled: draftId != null,
  });
}

export function useApproveIntakeDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, adjustments }: { draftId: number; adjustments?: Record<string, number> }) =>
      api.post(`/order-intake/queue/${draftId}/approve`, { adjustments }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-intake-queue'] });
      qc.invalidateQueries({ queryKey: ['order-intake-stats'] });
    },
  });
}

export function useRejectIntakeDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, reason }: { draftId: number; reason: string }) =>
      api.post(`/order-intake/queue/${draftId}/reject`, { reason }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-intake-queue'] });
      qc.invalidateQueries({ queryKey: ['order-intake-stats'] });
    },
  });
}

export function useOrderIntakeStats() {
  return useQuery({
    queryKey: ['order-intake-stats'],
    queryFn: () => api.get('/order-intake/stats').then(r => r.data),
    refetchInterval: 60_000,
  });
}

export function useReorderNudgesDue() {
  return useQuery({
    queryKey: ['order-intake-nudges-due'],
    queryFn: () => api.get('/order-intake/nudges/due').then(r => r.data),
  });
}

export function useSendReorderNudge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: number) =>
      api.post('/order-intake/nudges/send', { customer_id: customerId }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-intake-nudges-due'] }),
  });
}

// ---------------------------------------------------------------------------
// Automation Hub
// ---------------------------------------------------------------------------

export interface AutomationStats {
  total_matches: number;
  auto_approved: number;
  expiring_items: number;
  reorder_suggestions: number;
  pending_reviews: number;
}

export interface ExpiryAlert {
  id: number;
  product_name: string;
  lot_number: string;
  expiry_date: string;
  days_remaining: number;
  quantity: number;
  status: string;
}

export interface MatchResult {
  id: number;
  po_number: string;
  invoice_number: string;
  receipt_number: string;
  status: string;
  variance_amount: number;
  matched_at: string | null;
}

export interface ReorderSuggestion {
  id: number;
  product_name: string;
  current_stock: number;
  reorder_point: number;
  suggested_quantity: number;
  vendor_name: string;
  status: string;
}

export interface VendorComplianceRecord {
  id: number;
  vendor_name: string;
  compliance_type: string;
  status: string;
  score: number;
}

export function useAutomationStats() {
  return useQuery({
    queryKey: ['automation-stats'],
    queryFn: () => fetchOne<AutomationStats>('/automation/stats'),
  });
}

export function useExpiryAlerts() {
  return useQuery({
    queryKey: ['expiry-alerts'],
    queryFn: async () => {
      const { data } = await api.get<ExpiryAlert[]>('/automation/expiry-alerts');
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useMatchResults() {
  return useQuery({
    queryKey: ['match-results'],
    queryFn: async () => {
      const { data } = await api.get<MatchResult[]>('/automation/match-results');
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useReorderSuggestions() {
  return useQuery({
    queryKey: ['reorder-suggestions'],
    queryFn: async () => {
      const { data } = await api.get<ReorderSuggestion[]>('/automation/reorder-suggestions');
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useRunBatchMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/automation/batch-match'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['match-results'] });
      qc.invalidateQueries({ queryKey: ['automation-stats'] });
    },
  });
}

export function useRunAutoReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/automation/auto-reorder'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reorder-suggestions'] });
      qc.invalidateQueries({ queryKey: ['automation-stats'] });
    },
  });
}

export function useVendorCompliance() {
  return useQuery({
    queryKey: ['vendor-compliance'],
    queryFn: async () => {
      const { data } = await api.get<VendorComplianceRecord[]>('/automation/vendor-compliance');
      return Array.isArray(data) ? data : [];
    },
  });
}

// ---------------------------------------------------------------------------
// Recurring AP Rules
// ---------------------------------------------------------------------------

export interface RecurringAPRule {
  id: number;
  name: string;
  vendor_name: string;
  frequency: string;
  amount: number;
  gl_account: string;
  next_run: string | null;
  last_run: string | null;
  status: string;
}

export function useRecurringAPRules() {
  return useQuery({
    queryKey: ['recurring-ap-rules'],
    queryFn: async () => {
      const { data } = await api.get<RecurringAPRule[]>('/automation/recurring-ap-rules');
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useGenerateRecurringOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/recurring-orders/${id}/generate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-orders'] });
    },
  });
}

export function useRunRecurringAPRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/automation/recurring-ap-rules/${id}/run`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-ap-rules'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Workflow Engine
// ---------------------------------------------------------------------------

export interface WorkflowInstance {
  id: number;
  entity_type: string;
  entity_id: number;
  status: string;
  current_step: string;
  initiated_by: string;
  created_at: string | null;
}

export interface WorkflowPendingApproval {
  id: number;
  step_id: number;
  step_name: string | null;
  assigned_to: string;
  status: string;
  created_at: string | null;
}

export interface WorkflowStepInstance {
  id: number;
  step_id: number;
  step_name: string | null;
  assigned_to: string | null;
  status: string;
  decision: string | null;
  decided_at: string | null;
}

export function useWorkflowInstances() {
  return useQuery({
    queryKey: ['workflow-instances'],
    queryFn: async () => {
      const { data } = await api.get<WorkflowInstance[]>('/workflow-engine/instances');
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useWorkflowPendingApprovals() {
  return useQuery({
    queryKey: ['workflow-pending-approvals'],
    queryFn: async () => {
      const { data } = await api.get<WorkflowPendingApproval[]>('/workflow-engine/pending-approvals');
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useWorkflowStepInstances(instanceId: number) {
  return useQuery({
    queryKey: ['workflow-step-instances', instanceId],
    queryFn: async () => {
      const { data } = await api.get<WorkflowStepInstance[]>(
        `/workflow-engine/instances/${instanceId}/steps`,
      );
      return Array.isArray(data) ? data : [];
    },
    enabled: instanceId > 0,
  });
}

export function useDecideWorkflowStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stepId,
      decision,
      comment,
    }: {
      stepId: number;
      decision: string;
      comment?: string;
    }) => api.post(`/workflow-engine/steps/${stepId}/decide`, { decision, comment }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-instances'] });
      qc.invalidateQueries({ queryKey: ['workflow-pending-approvals'] });
      qc.invalidateQueries({ queryKey: ['workflow-step-instances'] });
    },
  });
}

export function useEscalateWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instanceId: number) =>
      api.post(`/workflow-engine/instances/${instanceId}/escalate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-instances'] });
      qc.invalidateQueries({ queryKey: ['workflow-pending-approvals'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Compliance Scheduler
// ---------------------------------------------------------------------------

export interface ComplianceSchedulerStatus {
  status: string;
  active_schedules: number;
  total_schedules: number;
  last_run: string | null;
  error_count: number;
}

export interface ComplianceSchedule {
  id: number;
  name: string;
  category: string;
  frequency: string;
  is_active: boolean;
}

export interface ComplianceScheduleHistoryEntry {
  id: number;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
}

export function useComplianceSchedulerStatus() {
  return useQuery({
    queryKey: ['compliance-scheduler-status'],
    queryFn: () => fetchOne<ComplianceSchedulerStatus>('/compliance/scheduler/status'),
  });
}

export function useComplianceSchedules() {
  return useQuery({
    queryKey: ['compliance-schedules'],
    queryFn: async () => {
      const { data } = await api.get<ComplianceSchedule[]>('/compliance/scheduler/schedules');
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useComplianceScheduleHistory(scheduleId: number) {
  return useQuery({
    queryKey: ['compliance-schedule-history', scheduleId],
    queryFn: async () => {
      const { data } = await api.get<ComplianceScheduleHistoryEntry[]>(
        `/compliance/scheduler/schedules/${scheduleId}/history`,
      );
      return Array.isArray(data) ? data : [];
    },
    enabled: scheduleId > 0,
  });
}

export function useTriggerComplianceSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: number) =>
      api.post(`/compliance/scheduler/schedules/${scheduleId}/trigger`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-schedules'] });
      qc.invalidateQueries({ queryKey: ['compliance-scheduler-status'] });
      qc.invalidateQueries({ queryKey: ['compliance-schedule-history'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Employee Self-Service
// ---------------------------------------------------------------------------

export function useEmployeeSelfService(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['employee-self-service', params],
    queryFn: () => fetchPaginated<any>('/hr/self-service', params),
  });
}

// ---------------------------------------------------------------------------
// Customer Timeline & Context (Agent Desktop)
// ---------------------------------------------------------------------------

export interface TimelineEvent {
  id: number;
  type: string;
  summary: string;
  details: string | null;
  channel: string | null;
  timestamp: string;
}

export interface CustomerContextData {
  recent_orders: {
    id: number;
    order_number: string;
    status: string;
    total: number | null;
    created_at: string | null;
  }[];
  open_invoices: {
    id: number;
    invoice_number: string;
    status: string;
    amount: number | null;
    days_old: number;
  }[];
  open_tickets: {
    id: number;
    ticket_number: string;
    subject: string;
    status: string;
    priority: string;
  }[];
  recent_calls: {
    id: number;
    direction: string;
    status: string;
    duration_seconds: number | null;
    sentiment: string | null;
    created_at: string | null;
  }[];
  predicted_reasons: string[];
  suggested_actions: string[];
}

export function useCustomerTimeline(customerId?: number) {
  return useQuery({
    queryKey: ['customer-timeline', customerId],
    queryFn: async () => {
      const { data } = await api.get<TimelineEvent[]>(
        `/service/customers/${customerId}/timeline`,
      );
      return Array.isArray(data) ? data : [];
    },
    enabled: !!customerId && customerId > 0,
  });
}

export function useCustomerContext(customerId?: number) {
  return useQuery({
    queryKey: ['customer-context', customerId],
    queryFn: () => fetchOne<CustomerContextData>(`/service/customers/${customerId}/context`),
    enabled: !!customerId && customerId > 0,
  });
}

// ---------------------------------------------------------------------------
// Knowledge Base
// ---------------------------------------------------------------------------

export interface KBArticle {
  id: number;
  title: string;
  content: string;
  summary: string | null;
  category: string;
  tags: string[] | null;
  is_published: boolean;
  view_count: number;
  helpful_count: number;
  not_helpful_count: number;
  created_at: string;
  updated_at: string;
}

export interface KBSearchResult {
  id: number;
  title: string;
  summary: string | null;
  content: string;
  category: string;
  score?: number;
}

export function useKBSearch(query?: string) {
  return useQuery({
    queryKey: ['kb-search', query],
    queryFn: async () => {
      const { data } = await api.get<KBSearchResult[]>('/service/kb/search', {
        params: { q: query },
      });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!query && query.length >= 2,
  });
}

export function useKBArticles(params?: {
  category?: string;
  is_published?: boolean;
  search?: string;
}) {
  return useQuery({
    queryKey: ['kb-articles', params],
    queryFn: async () => {
      const { data } = await api.get<KBArticle[]>('/service/kb/articles', { params });
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useKBArticle(id?: number) {
  return useQuery({
    queryKey: ['kb-articles', id],
    queryFn: () => fetchOne<KBArticle>(`/service/kb/articles/${id}`),
    enabled: !!id && id > 0,
  });
}

export function useCreateKBArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<KBArticle>) => api.post('/service/kb/articles', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kb-articles'] }),
  });
}

export function useUpdateKBArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<KBArticle> & { id: number }) =>
      api.patch(`/service/kb/articles/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kb-articles'] }),
  });
}

export function useDeleteKBArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/service/kb/articles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kb-articles'] }),
  });
}

export function useRateKBArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, helpful }: { id: number; helpful: boolean }) =>
      api.post(`/service/kb/articles/${id}/rate`, { helpful }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kb-articles'] }),
  });
}

// ---------------------------------------------------------------------------
// Support Tickets
// ---------------------------------------------------------------------------

export interface SupportTicket {
  id: number;
  ticket_number: string;
  customer_id: number;
  subject: string;
  description: string | null;
  status: string;
  priority: string;
  category: string;
  channel: string;
  assigned_to: string | null;
  sla_due_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TicketComment {
  id: number;
  ticket_id: number;
  author: string;
  content: string;
  is_internal: boolean;
  created_at: string;
}

export interface TicketMetrics {
  open_count: number;
  in_progress_count: number;
  overdue_count: number;
  avg_resolution_hours: number;
}

export function useTickets(params?: {
  status?: string;
  priority?: string;
  assigned_to?: string;
}) {
  return useQuery({
    queryKey: ['tickets', params],
    queryFn: async () => {
      const { data } = await api.get<SupportTicket[]>('/service/tickets', { params });
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useTicket(id?: number) {
  return useQuery({
    queryKey: ['tickets', id],
    queryFn: () => fetchOne<SupportTicket>(`/service/tickets/${id}`),
    enabled: !!id && id > 0,
  });
}

export function useTicketComments(ticketId?: number) {
  return useQuery({
    queryKey: ['ticket-comments', ticketId],
    queryFn: async () => {
      const { data } = await api.get<TicketComment[]>(
        `/service/tickets/${ticketId}/comments`,
      );
      return Array.isArray(data) ? data : [];
    },
    enabled: !!ticketId && ticketId > 0,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      customer_id: number;
      subject: string;
      description?: string;
      priority?: string;
      category?: string;
      channel?: string;
    }) => api.post('/service/tickets', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket-metrics'] });
    },
  });
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: number; status?: string; priority?: string; category?: string }) =>
      api.patch(`/service/tickets/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket-metrics'] });
    },
  });
}

export function useAssignTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, assigned_to }: { ticketId: number; assigned_to: string }) =>
      api.post(`/service/tickets/${ticketId}/assign`, { assigned_to }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}

export function useCloseTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: number) => api.post(`/service/tickets/${ticketId}/close`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket-metrics'] });
    },
  });
}

export function useAddTicketComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      ticketId,
      content,
      is_internal,
    }: {
      ticketId: number;
      content: string;
      is_internal?: boolean;
    }) => api.post(`/service/tickets/${ticketId}/comments`, { content, is_internal }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket-comments'] });
    },
  });
}

export function useTicketMetrics() {
  return useQuery({
    queryKey: ['ticket-metrics'],
    queryFn: () => fetchOne<TicketMetrics>('/service/tickets/metrics'),
  });
}

export * from './api';
// ==================================================================
//  AI Customer Automation
// ==================================================================

export function useAITriageTicket(ticketId: number) {
  return useQuery({
    queryKey: ['ai-triage-ticket', ticketId],
    queryFn: () => api.get(`/ai-customer/support/triage/${ticketId}`).then(r => r.data),
    enabled: ticketId > 0,
  });
}

export function useAIBatchTriage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (limit: number = 50) =>
      api.post(`/ai-customer/support/batch-triage?limit=${limit}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-tickets'] }),
  });
}

export function useAIAnalyzeClaim(claimId: number) {
  return useQuery({
    queryKey: ['ai-analyze-claim', claimId],
    queryFn: () => api.get(`/ai-customer/claims/analyze/${claimId}`).then(r => r.data),
    enabled: claimId > 0,
  });
}

export function useAIAdjudicateClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ claimId, threshold }: { claimId: number; threshold?: number }) =>
      api.post(`/ai-customer/claims/adjudicate/${claimId}?threshold=${threshold ?? 200}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quality-claims'] }),
  });
}

export function useAIClaimAlert(customerId: number) {
  return useQuery({
    queryKey: ['ai-claim-alert', customerId],
    queryFn: () => api.get(`/ai-customer/claims/alert/${customerId}`).then(r => r.data),
    enabled: customerId > 0,
  });
}

export function useAIBatchClaimAlerts() {
  return useQuery({
    queryKey: ['ai-batch-claim-alerts'],
    queryFn: () => api.get('/ai-customer/claims/batch-alerts').then(r => r.data),
  });
}

export function useAIFraudCheck(customerId: number) {
  return useQuery({
    queryKey: ['ai-fraud-check', customerId],
    queryFn: () => api.get(`/ai-customer/claims/fraud-check/${customerId}`).then(r => r.data),
    enabled: customerId > 0,
  });
}

export function useAIDisputeAnalysis(disputeId: number) {
  return useQuery({
    queryKey: ['ai-dispute-analysis', disputeId],
    queryFn: () => api.get(`/ai-customer/disputes/analyze/${disputeId}`).then(r => r.data),
    enabled: disputeId > 0,
  });
}

export function useAIAutoResolveDispute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (disputeId: number) =>
      api.post(`/ai-customer/disputes/auto-resolve/${disputeId}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['disputes'] }),
  });
}

export function useAICustomerRiskScore(customerId: number) {
  return useQuery({
    queryKey: ['ai-customer-risk', customerId],
    queryFn: () => api.get(`/ai-customer/onboarding/risk/${customerId}`).then(r => r.data),
    enabled: customerId > 0,
  });
}

export function useAIAutoApproveCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: number) =>
      api.post(`/ai-customer/onboarding/approve/${customerId}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useAIBatchOnboarding() {
  return useMutation({
    mutationFn: () =>
      api.post('/ai-customer/onboarding/batch-process').then(r => r.data),
  });
}

export function useAIPaymentRisk(customerId: number) {
  return useQuery({
    queryKey: ['ai-payment-risk', customerId],
    queryFn: () => api.get(`/ai-customer/payments/risk/${customerId}`).then(r => r.data),
    enabled: customerId > 0,
  });
}

export function useAIDeliveryRisk(orderId: number) {
  return useQuery({
    queryKey: ['ai-delivery-risk', orderId],
    queryFn: () => api.get(`/ai-customer/delivery/risk/${orderId}`).then(r => r.data),
    enabled: orderId > 0,
  });
}

export function useAIOrderModEvaluate() {
  return useMutation({
    mutationFn: (body: { order_id: number; changes: any[] }) =>
      api.post('/ai-customer/orders/evaluate-mod', body).then(r => r.data),
  });
}

export function useAIDocComplianceDashboard(customerId?: number) {
  return useQuery({
    queryKey: ['ai-doc-compliance', customerId],
    queryFn: () => api.get('/ai-customer/compliance/dashboard', { params: { customer_id: customerId } }).then(r => r.data),
  });
}

export function useAIAccountHealth(customerId: number) {
  return useQuery({
    queryKey: ['ai-account-health', customerId],
    queryFn: () => api.get(`/ai-customer/account/health/${customerId}`).then(r => r.data),
    enabled: customerId > 0,
  });
}

export function useAICustomerSegmentation() {
  return useQuery({
    queryKey: ['ai-customer-segmentation'],
    queryFn: () => api.get('/ai-customer/account/segmentation').then(r => r.data),
  });
}

export function useAIDraftEmail() {
  return useMutation({
    mutationFn: (body: { customer_id: number; purpose: string; context?: Record<string, any> }) =>
      api.post('/ai-customer/comms/draft-email', body).then(r => r.data),
  });
}

export function useAIBatchOutreach() {
  return useMutation({
    mutationFn: (body: { trigger_type: string; limit?: number }) =>
      api.post('/ai-customer/comms/batch-outreach', body).then(r => r.data),
  });
}

export function useAIPriceAlerts() {
  return useQuery({
    queryKey: ['ai-price-alerts'],
    queryFn: () => api.get('/ai-customer/pricing/alerts').then(r => r.data),
  });
}

// ---------------------------------------------------------------------------
// Operational Feedback
// ---------------------------------------------------------------------------

export function useOperationalAlerts(params?: {
  role?: string;
  status?: string;
  customer_id?: number;
  severity?: string;
  page?: number;
  per_page?: number;
}) {
  return useQuery({
    queryKey: ['operational-alerts', params],
    queryFn: () => api.get('/operational-feedback/alerts', { params }).then(r => r.data),
    refetchInterval: 30_000,
  });
}

export function useMyAlerts(status = 'active') {
  return useQuery({
    queryKey: ['my-alerts', status],
    queryFn: () => api.get('/operational-feedback/my-alerts', { params: { status } }).then(r => r.data),
    refetchInterval: 30_000,
  });
}

export function useOperationalAlert(id: number | null) {
  return useQuery({
    queryKey: ['operational-alert', id],
    queryFn: () => api.get(`/operational-feedback/alerts/${id}`).then(r => r.data),
    enabled: id !== null,
  });
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) =>
      api.post(`/operational-feedback/alerts/${alertId}/acknowledge`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-alerts'] });
      qc.invalidateQueries({ queryKey: ['my-alerts'] });
      qc.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });
}

export function useResolveAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, notes }: { alertId: number; notes?: string }) =>
      api.post(`/operational-feedback/alerts/${alertId}/resolve`, { notes }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-alerts'] });
      qc.invalidateQueries({ queryKey: ['my-alerts'] });
      qc.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });
}

export function useDismissOperationalAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) =>
      api.post(`/operational-feedback/alerts/${alertId}/dismiss`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-alerts'] });
      qc.invalidateQueries({ queryKey: ['my-alerts'] });
      qc.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });
}

export function useCustomerIntelligence(customerId: number | null) {
  return useQuery({
    queryKey: ['customer-intelligence', customerId],
    queryFn: () =>
      api.get(`/operational-feedback/customer/${customerId}/intelligence`).then(r => r.data),
    enabled: customerId !== null,
  });
}

export function useAlertStats() {
  return useQuery({
    queryKey: ['alert-stats'],
    queryFn: () => api.get('/operational-feedback/stats').then(r => r.data),
    refetchInterval: 60_000,
  });
}

export function useTriggerCustomerAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: number) =>
      api.post(`/operational-feedback/analyze/customer/${customerId}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-alerts'] });
      qc.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });
}

export function useTriggerBatchAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: { days?: number; limit?: number }) =>
      api.post('/operational-feedback/analyze/batch', body ?? {}).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-alerts'] });
      qc.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });
}

